import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Select,
  Input,
  Button,
  HStack,
  VStack,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar, faUsers, faSignInAlt, faChartLine, faCalendarCheck, faShoppingCart, faBoxOpen, faIndustry, faSyringe, faMoneyBillWave, faFileAlt } from "@fortawesome/free-solid-svg-icons";
import * as IconButton from "../../themes/IconButtonSet";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันแปลงเลขเดือนเป็นชื่อเดือนภาษาไทย
const getThaiMonth = (monthNumber) => {
  const thaiMonths = [
    "มกราคม",
    "กุมภาพันธ์",
    "มีนาคม",
    "เมษายน",
    "พฤษภาคม",
    "มิถุนายน",
    "กรกฎาคม",
    "สิงหาคม",
    "กันยายน",
    "ตุลาคม",
    "พฤศจิกายน",
    "ธันวาคม",
  ];
  return thaiMonths[monthNumber - 1] || "";
};

// ฟังก์ชันแปลงวันที่เป็นภาษาไทยและปีพ.ศ.
const formatThaiDate = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear() + 543; // แปลงเป็นปีพ.ศ.
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day} ${getThaiMonth(month)} ${year} ${hours}:${minutes}`;
  } catch (error) {
    return "-";
  }
};

// ฟังก์ชันสร้างรายการเดือน/ปี
const generateMonthYearOptions = () => {
  const options = [];
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  // สร้างรายการ 2 ปีย้อนหลัง
  for (let year = currentYear - 1; year <= currentYear + 1; year++) {
    for (let month = 1; month <= 12; month++) {
      options.push({
        value: `${year}-${month.toString().padStart(2, '0')}`,
        label: `${getThaiMonth(month)} ${year}`,
        year: year,
        month: month,
      });
    }
  }
  return options;
};

const SystemUsageDashboard = () => {
  const { sessionId } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [offices, setOffices] = useState([]);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalData, setModalData] = useState([]);
  const [modalVaccineData, setModalVaccineData] = useState([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [modalFilter, setModalFilter] = useState({
    officeName: "",
    pickupPointName: "",
    warehouseName: "",
  });
  // สำหรับเก็บข้อมูลทุกประเภทกิจกรรมของเดือน
  const [monthActivityData, setMonthActivityData] = useState({
    reservations: [],
    sales: [],
    requisitions: [],
    productionReceives: [],
    offices: [],
  });

  // สำหรับเก็บข้อมูลกราฟแยกตามชนิดวัคซีน
  const [vaccineChartData, setVaccineChartData] = useState({
    reservation: [],
    sale: [],
    requisition: [],
    productionReceive: [],
  });
  
  // คำนวณปีพ.ศ.ปัจจุบัน
  const currentBuddhistYear = new Date().getFullYear() + 543;
  
  // คำนวณเดือนเริ่มต้น (ย้อนหลัง 12 เดือน)
  const currentDate = new Date();
  const startDate = new Date(currentDate);
  startDate.setMonth(currentDate.getMonth() - 11); // ย้อนหลัง 11 เดือน + เดือนปัจจุบัน = 12 เดือน
  
  const [filters, setFilters] = useState({
    StartMonth: startDate.getMonth() + 1,
    StartYear: startDate.getFullYear() + 543, // ใช้ปีพ.ศ.
    EndMonth: currentDate.getMonth() + 1,
    EndYear: currentBuddhistYear, // ใช้ปีพ.ศ.
    OfficeID: "",
  });

  const monthYearOptions = generateMonthYearOptions();
  const cardBg = useColorModeValue("white", "gray.800");

  // ดึงรายการหน่วยงาน
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await apiService({
          method: "POST",
          url: "/getDLDOfficeList",
          data: {},
          sessionId: sessionId || "",
        });
        if (response.result === "success") {
          setOffices(response.OfficeList || []);
        }
      } catch (error) {
        console.error("Error fetching offices:", error);
      }
    };

    if (sessionId) {
      fetchOffices();
    }
  }, [sessionId]);


  // ดึงข้อมูล Dashboard
  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      // แปลงปีพ.ศ. เป็นปีค.ศ. สำหรับส่งไปยัง Backend
      const startYearAD = filters.StartYear - 543;
      const endYearAD = filters.EndYear - 543;
      
      const response = await apiService({
        method: "POST",
        url: "/getSystemUsageDashboard",
        data: {
          StartMonth: filters.StartMonth.toString().padStart(2, '0'),
          StartYear: startYearAD.toString(),
          EndMonth: filters.EndMonth.toString().padStart(2, '0'),
          EndYear: endYearAD.toString(),
          OfficeID: filters.OfficeID || null,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        console.log("Dashboard Data received:", response);
        console.log("MonthlyStats:", response.MonthlyStats);
        setDashboardData(response);
      } else {
        alert("ไม่พบข้อมูล: " + response.error);
        setDashboardData(null);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchDashboardData();
      fetchVaccineChartData();
    }
  }, [sessionId]);

  const handleFilterChange = (name, value) => {
    setFilters({ ...filters, [name]: value });
  };

  const handleSearch = () => {
    fetchDashboardData();
    fetchVaccineChartData();
  };

  // ฟังก์ชันดึงข้อมูลกราฟแยกตามชนิดวัคซีน
  const fetchVaccineChartData = async () => {
    try {
      const startYearAD = filters.StartYear - 543;
      const endYearAD = filters.EndYear - 543;

      const commonData = {
        StartMonth: filters.StartMonth.toString().padStart(2, '0'),
        StartYear: startYearAD.toString(),
        EndMonth: filters.EndMonth.toString().padStart(2, '0'),
        EndYear: endYearAD.toString(),
        OfficeID: filters.OfficeID || null,
      };

      const [reservationRes, saleRes, requisitionRes, productionReceiveRes] = await Promise.all([
        apiService({ method: "POST", url: "/getReservationByVaccine", data: commonData, sessionId }),
        apiService({ method: "POST", url: "/getSaleByVaccine", data: commonData, sessionId }),
        apiService({ method: "POST", url: "/getRequisitionByVaccine", data: commonData, sessionId }),
        apiService({ method: "POST", url: "/getProductionReceiveByVaccine", data: commonData, sessionId }),
      ]);

      setVaccineChartData({
        reservation: reservationRes.result === "success" ? (reservationRes.VaccineList || []) : [],
        sale: saleRes.result === "success" ? (saleRes.VaccineList || []) : [],
        requisition: requisitionRes.result === "success" ? (requisitionRes.VaccineList || []) : [],
        productionReceive: productionReceiveRes.result === "success" ? (productionReceiveRes.VaccineList || []) : [],
      });
    } catch (error) {
      console.error("Error fetching vaccine chart data:", error);
      setVaccineChartData({ reservation: [], sale: [], requisition: [], productionReceive: [] });
    }
  };

  // ฟังก์ชันดึงรายละเอียดหน่วยงานที่ใช้ระบบ (ภาพรวม)
  const handleViewOfficeDetails = async (monthNameOrYear = null) => {
    setIsLoadingDetails(true);
    setIsModalOpen(true);
    // ตรวจสอบว่า monthNameOrYear เป็น string หรือไม่
    const monthNameStr = (typeof monthNameOrYear === 'string' && monthNameOrYear) ? monthNameOrYear : null;
    setModalTitle(monthNameStr ? `รายละเอียดหน่วยงานที่ใช้ระบบ - ${monthNameStr}` : "รายละเอียดหน่วยงานที่ใช้ระบบ (ภาพรวม)");
    setModalFilter({ officeName: "", pickupPointName: "", warehouseName: "" });
    
    // แปลง monthName (เช่น "มกราคม 2567") เป็น MonthYear (เช่น "2024-01")
    let monthYearForAPI = null;
    if (monthNameStr) {
      // หา monthYear จาก chartData ที่ตรงกับ monthName
      const matchedData = chartData.find(item => item.month === monthNameStr);
      if (matchedData) {
        monthYearForAPI = matchedData.monthYear;
      }
    }
    
    try {
      // แปลงปีพ.ศ. เป็นปีค.ศ. สำหรับส่งไปยัง Backend
      const startYearAD = filters.StartYear - 543;
      const endYearAD = filters.EndYear - 543;
      
      const response = await apiService({
        method: "POST",
        url: "/getOfficeUsageDetails",
        data: {
          StartMonth: filters.StartMonth.toString().padStart(2, '0'),
          StartYear: startYearAD.toString(),
          EndMonth: filters.EndMonth.toString().padStart(2, '0'),
          EndYear: endYearAD.toString(),
          OfficeID: filters.OfficeID || null,
          MonthYear: monthYearForAPI || null, // ส่ง MonthYear (รูปแบบ yyyy-MM) ถ้ามี
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setModalData(response.OfficeList || []);
      } else {
        alert("ไม่พบข้อมูล: " + response.error);
        setModalData([]);
      }
    } catch (error) {
      console.error("Error fetching office details:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
      setModalData([]);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // ฟังก์ชันจัดการการคลิกที่แท่งกราฟ
  const handleBarClick = (data, index) => {
    if (data && chartData[index]) {
      // ใช้ monthYear จาก chartData
      const monthYear = chartData[index].monthYear;
      const monthName = chartData[index].month;
      // ส่ง monthName (ชื่อเดือนภาษาไทย + ปีพ.ศ.) แทน monthYear
      handleViewOfficeDetails(monthName);
    }
  };

  // ฟังก์ชันจัดการการคลิกที่เดือนในกราฟเปรียบเทียบกิจกรรม - แสดงข้อมูลทุกประเภทกิจกรรม
  const handleMonthClick = async (monthName) => {
    if (!monthName || !chartData) return;
    
    // หา monthYear จาก chartData
    const monthData = chartData.find(item => item.month === monthName);
    if (!monthData || !monthData.monthYear) return;
    
    setIsLoadingDetails(true);
    setIsModalOpen(true);
    setModalTitle(`รายละเอียดกิจกรรมทั้งหมด - ${monthName}`);
    setModalFilter({ officeName: "", pickupPointName: "", warehouseName: "" });
    
    try {
      // แปลงปีพ.ศ. เป็นปีค.ศ. สำหรับส่งไปยัง Backend
      const startYearAD = filters.StartYear - 543;
      const endYearAD = filters.EndYear - 543;
      
      // ดึงข้อมูลทุกประเภทกิจกรรมพร้อมกัน
      const [officeRes, reservationRes, saleRes, requisitionRes, productionReceiveRes] = await Promise.all([
        // หน่วยงานที่ใช้ระบบ
        apiService({
          method: "POST",
          url: "/getOfficeUsageDetails",
          data: {
            StartMonth: filters.StartMonth.toString().padStart(2, '0'),
            StartYear: startYearAD.toString(),
            EndMonth: filters.EndMonth.toString().padStart(2, '0'),
            EndYear: endYearAD.toString(),
            OfficeID: filters.OfficeID || null,
            MonthYear: monthData.monthYear,
          },
          sessionId: sessionId || "",
        }),
        // การสั่งจอง
        apiService({
          method: "POST",
          url: "/getReservationSaleDetails",
          data: {
            StartMonth: filters.StartMonth.toString().padStart(2, '0'),
            StartYear: startYearAD.toString(),
            EndMonth: filters.EndMonth.toString().padStart(2, '0'),
            EndYear: endYearAD.toString(),
            OfficeID: filters.OfficeID || null,
            Type: 'reservation',
            MonthYear: monthData.monthYear,
          },
          sessionId: sessionId || "",
        }),
        // การขาย
        apiService({
          method: "POST",
          url: "/getReservationSaleDetails",
          data: {
            StartMonth: filters.StartMonth.toString().padStart(2, '0'),
            StartYear: startYearAD.toString(),
            EndMonth: filters.EndMonth.toString().padStart(2, '0'),
            EndYear: endYearAD.toString(),
            OfficeID: filters.OfficeID || null,
            Type: 'sale',
            MonthYear: monthData.monthYear,
          },
          sessionId: sessionId || "",
        }),
        // การเบิกจ่าย
        apiService({
          method: "POST",
          url: "/getRequisitionDetails",
          data: {
            StartMonth: filters.StartMonth.toString().padStart(2, '0'),
            StartYear: startYearAD.toString(),
            EndMonth: filters.EndMonth.toString().padStart(2, '0'),
            EndYear: endYearAD.toString(),
            OfficeID: filters.OfficeID || null,
            MonthYear: monthData.monthYear,
          },
          sessionId: sessionId || "",
        }),
        // การรับวัคซีนจากการผลิต
        apiService({
          method: "POST",
          url: "/getProductionReceiveDetails",
          data: {
            StartMonth: filters.StartMonth.toString().padStart(2, '0'),
            StartYear: startYearAD.toString(),
            EndMonth: filters.EndMonth.toString().padStart(2, '0'),
            EndYear: endYearAD.toString(),
            OfficeID: filters.OfficeID || null,
            MonthYear: monthData.monthYear,
          },
          sessionId: sessionId || "",
        }),
      ]);
      
      // เก็บข้อมูลทุกประเภทกิจกรรม
      setMonthActivityData({
        offices: officeRes.result === "success" ? (officeRes.OfficeList || []) : [],
        reservations: reservationRes.result === "success" ? (reservationRes.DetailsList || []) : [],
        reservationVaccines: reservationRes.result === "success" ? (reservationRes.VaccineDetailsList || []) : [],
        sales: saleRes.result === "success" ? (saleRes.DetailsList || []) : [],
        saleVaccines: saleRes.result === "success" ? (saleRes.VaccineDetailsList || []) : [],
        requisitions: requisitionRes.result === "success" ? (requisitionRes.DetailsList || []) : [],
        requisitionVaccines: requisitionRes.result === "success" ? (requisitionRes.VaccineDetailsList || []) : [],
        productionReceives: productionReceiveRes.result === "success" ? (productionReceiveRes.DetailsList || []) : [],
        productionReceiveVaccines: productionReceiveRes.result === "success" ? (productionReceiveRes.VaccineDetailsList || []) : [],
      });
      
      // ตั้งค่า modalData เป็นข้อมูลหน่วยงานก่อน (เพื่อแสดงตารางแรก)
      setModalData(officeRes.result === "success" ? (officeRes.OfficeList || []) : []);
      
    } catch (error) {
      console.error("Error fetching month activity details:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
      setMonthActivityData({ offices: [], reservations: [], sales: [], requisitions: [], productionReceives: [] });
      setModalData([]);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Custom Tick component สำหรับ XAxis ที่สามารถคลิกได้
  const CustomTick = (props) => {
    const { x, y, payload } = props;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#666"
          fontSize={12}
          transform="rotate(-45)"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (payload && payload.value) {
              handleMonthClick(payload.value);
            }
          }}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  // ฟังก์ชันดึงรายละเอียดการสั่งจอง/การขาย
  const handleViewReservationSaleDetails = async (type) => {
    setIsLoadingDetails(true);
    setIsModalOpen(true);
    setModalTitle(type === 'reservation' ? "รายละเอียดการสั่งจองวัคซีน (ตามจุดจำหน่าย)" : "รายละเอียดการขายวัคซีน (ตามจุดจำหน่าย)");
    setModalFilter({ officeName: "", pickupPointName: "", warehouseName: "" });
    try {
      // แปลงปีพ.ศ. เป็นปีค.ศ. สำหรับส่งไปยัง Backend
      const startYearAD = filters.StartYear - 543;
      const endYearAD = filters.EndYear - 543;
      
      const response = await apiService({
        method: "POST",
        url: "/getReservationSaleDetails",
        data: {
          StartMonth: filters.StartMonth.toString().padStart(2, '0'),
          StartYear: startYearAD.toString(),
          EndMonth: filters.EndMonth.toString().padStart(2, '0'),
          EndYear: endYearAD.toString(),
          OfficeID: filters.OfficeID || null,
          Type: type,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setModalData(response.DetailsList || []);
        setModalVaccineData(response.VaccineDetailsList || []);
      } else {
        alert("ไม่พบข้อมูล: " + response.error);
        setModalData([]);
        setModalVaccineData([]);
      }
    } catch (error) {
      console.error("Error fetching reservation/sale details:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
      setModalData([]);
      setModalVaccineData([]);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // ฟังก์ชันดึงรายละเอียดการเบิกจ่ายวัคซีน
  const handleViewRequisitionDetails = async (monthNameOrYear = null) => {
    setIsLoadingDetails(true);
    setIsModalOpen(true);
    const monthNameStr = (typeof monthNameOrYear === 'string' && monthNameOrYear) ? monthNameOrYear : null;
    setModalTitle(monthNameStr ? `รายละเอียดการเบิกจ่ายวัคซีน (ตามคลังปลายทาง) - ${monthNameStr}` : "รายละเอียดการเบิกจ่ายวัคซีน (ตามคลังปลายทาง)");
    setModalFilter({ officeName: "", pickupPointName: "", warehouseName: "" });
    
    // แปลง monthName เป็น MonthYear
    let monthYearForAPI = null;
    if (monthNameStr) {
      const matchedData = chartData.find(item => item.month === monthNameStr);
      if (matchedData) {
        monthYearForAPI = matchedData.monthYear;
      }
    }
    
    try {
      // แปลงปีพ.ศ. เป็นปีค.ศ. สำหรับส่งไปยัง Backend
      const startYearAD = filters.StartYear - 543;
      const endYearAD = filters.EndYear - 543;
      
      const response = await apiService({
        method: "POST",
        url: "/getRequisitionDetails",
        data: {
          StartMonth: filters.StartMonth.toString().padStart(2, '0'),
          StartYear: startYearAD.toString(),
          EndMonth: filters.EndMonth.toString().padStart(2, '0'),
          EndYear: endYearAD.toString(),
          OfficeID: filters.OfficeID || null,
          MonthYear: monthYearForAPI || null,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setModalData(response.DetailsList || []);
        setModalVaccineData(response.VaccineDetailsList || []);
      } else {
        alert("ไม่พบข้อมูล: " + response.error);
        setModalData([]);
        setModalVaccineData([]);
      }
    } catch (error) {
      console.error("Error fetching requisition details:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
      setModalData([]);
      setModalVaccineData([]);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // ฟังก์ชันดึงรายละเอียดการรับวัคซีนจากฝ่ายผลิต
  const handleViewProductionReceiveDetails = async (monthNameOrYear = null) => {
    setIsLoadingDetails(true);
    setIsModalOpen(true);
    const monthNameStr = (typeof monthNameOrYear === 'string' && monthNameOrYear) ? monthNameOrYear : null;
    setModalTitle(monthNameStr ? `รายละเอียดการรับวัคซีนจากการผลิต - ${monthNameStr}` : "รายละเอียดการรับวัคซีนจากการผลิต");
    setModalFilter({ officeName: "", pickupPointName: "", warehouseName: "" });
    
    // แปลง monthName เป็น MonthYear
    let monthYearForAPI = null;
    if (monthNameStr) {
      const matchedData = chartData.find(item => item.month === monthNameStr);
      if (matchedData) {
        monthYearForAPI = matchedData.monthYear;
      }
    }
    
    try {
      // แปลงปีพ.ศ. เป็นปีค.ศ. สำหรับส่งไปยัง Backend
      const startYearAD = filters.StartYear - 543;
      const endYearAD = filters.EndYear - 543;
      
      const response = await apiService({
        method: "POST",
        url: "/getProductionReceiveDetails",
        data: {
          StartMonth: filters.StartMonth.toString().padStart(2, '0'),
          StartYear: startYearAD.toString(),
          EndMonth: filters.EndMonth.toString().padStart(2, '0'),
          EndYear: endYearAD.toString(),
          OfficeID: filters.OfficeID || null,
          MonthYear: monthYearForAPI || null,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setModalData(response.DetailsList || []);
        setModalVaccineData(response.VaccineDetailsList || []);
      } else {
        alert("ไม่พบข้อมูล: " + response.error);
        setModalData([]);
        setModalVaccineData([]);
      }
    } catch (error) {
      console.error("Error fetching production receive details:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
      setModalData([]);
      setModalVaccineData([]);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // ฟังก์ชันดึงรายละเอียดการสั่งจอง/การขาย (รองรับ monthName)
  const handleViewReservationSaleDetailsWithMonth = async (type, monthNameOrYear = null) => {
    setIsLoadingDetails(true);
    setIsModalOpen(true);
    const monthNameStr = (typeof monthNameOrYear === 'string' && monthNameOrYear) ? monthNameOrYear : null;
    setModalTitle(monthNameStr 
      ? (type === 'reservation' ? `รายละเอียดการสั่งจองวัคซีน (ตามจุดจำหน่าย) - ${monthNameStr}` : `รายละเอียดการขายวัคซีน (ตามจุดจำหน่าย) - ${monthNameStr}`)
      : (type === 'reservation' ? "รายละเอียดการสั่งจองวัคซีน (ตามจุดจำหน่าย)" : "รายละเอียดการขายวัคซีน (ตามจุดจำหน่าย)"));
    setModalFilter({ officeName: "", pickupPointName: "", warehouseName: "" });
    
    // แปลง monthName เป็น MonthYear
    let monthYearForAPI = null;
    if (monthNameStr) {
      const matchedData = chartData.find(item => item.month === monthNameStr);
      if (matchedData) {
        monthYearForAPI = matchedData.monthYear;
      }
    }
    
    try {
      // แปลงปีพ.ศ. เป็นปีค.ศ. สำหรับส่งไปยัง Backend
      const startYearAD = filters.StartYear - 543;
      const endYearAD = filters.EndYear - 543;
      
      const response = await apiService({
        method: "POST",
        url: "/getReservationSaleDetails",
        data: {
          StartMonth: filters.StartMonth.toString().padStart(2, '0'),
          StartYear: startYearAD.toString(),
          EndMonth: filters.EndMonth.toString().padStart(2, '0'),
          EndYear: endYearAD.toString(),
          OfficeID: filters.OfficeID || null,
          Type: type,
          MonthYear: monthYearForAPI || null,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setModalData(response.DetailsList || []);
        setModalVaccineData(response.VaccineDetailsList || []);
      } else {
        alert("ไม่พบข้อมูล: " + response.error);
        setModalData([]);
        setModalVaccineData([]);
      }
    } catch (error) {
      console.error("Error fetching reservation/sale details:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
      setModalData([]);
      setModalVaccineData([]);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // ฟังก์ชันจัดการการคลิกที่แท่งกราฟสำหรับแต่ละประเภท
  const handleBarClickReservation = (data, index) => {
    if (data && chartData[index]) {
      const monthName = chartData[index].month;
      handleViewReservationSaleDetailsWithMonth('reservation', monthName);
    }
  };

  const handleBarClickSale = (data, index) => {
    if (data && chartData[index]) {
      const monthName = chartData[index].month;
      handleViewReservationSaleDetailsWithMonth('sale', monthName);
    }
  };

  const handleBarClickRequisition = (data, index) => {
    if (data && chartData[index]) {
      const monthName = chartData[index].month;
      handleViewRequisitionDetails(monthName);
    }
  };

  const handleBarClickProductionReceive = (data, index) => {
    if (data && chartData[index]) {
      const monthName = chartData[index].month;
      handleViewProductionReceiveDetails(monthName);
    }
  };

  // ฟังก์ชันแปลง MonthName จาก Backend (เช่น "January 2024") เป็นภาษาไทยและปีพ.ศ. (เช่น "มกราคม 2567")
  const convertMonthNameToThai = (monthName) => {
    if (!monthName) return "";
    
    try {
      // monthName format: "January 2024" หรือ "มกราคม 2567" (ถ้าแปลงแล้ว)
      const parts = monthName.split(" ");
      if (parts.length < 2) return monthName;
      
      const monthNameEN = parts[0];
      const yearAD = parseInt(parts[1]);
      
      // ตรวจสอบว่าถูกแปลงแล้วหรือยัง (ถ้ามีคำว่า "มกราคม" อยู่แล้ว แสดงว่าแปลงแล้ว)
      if (monthNameEN.includes("มกราคม") || monthNameEN.includes("กุมภาพันธ์") || 
          monthNameEN.includes("มีนาคม") || monthNameEN.includes("เมษายน")) {
        return monthName; // ถ้าแปลงแล้วให้คืนค่าเดิม
      }
      
      // แปลงชื่อเดือนภาษาอังกฤษเป็นภาษาไทย
      const monthMap = {
        "January": "มกราคม",
        "February": "กุมภาพันธ์",
        "March": "มีนาคม",
        "April": "เมษายน",
        "May": "พฤษภาคม",
        "June": "มิถุนายน",
        "July": "กรกฎาคม",
        "August": "สิงหาคม",
        "September": "กันยายน",
        "October": "ตุลาคม",
        "November": "พฤศจิกายน",
        "December": "ธันวาคม",
      };
      
      const thaiMonth = monthMap[monthNameEN] || monthNameEN;
      
      // ตรวจสอบว่า yearAD เป็นตัวเลขที่ถูกต้องหรือไม่
      if (isNaN(yearAD) || yearAD < 1900 || yearAD > 2100) {
        return monthName; // ถ้าปีไม่ถูกต้องให้คืนค่าเดิม
      }
      
      const buddhistYear = yearAD + 543; // แปลงเป็นปีพ.ศ.
      
      return `${thaiMonth} ${buddhistYear}`;
    } catch (error) {
      console.error("Error converting month name:", error, monthName);
      return monthName; // ถ้ามี error ให้คืนค่าเดิม
    }
  };

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = useMemo(() => {
    if (!dashboardData?.MonthlyStats || dashboardData.MonthlyStats.length === 0) {
      console.log("No MonthlyStats data available", dashboardData);
      return [];
    }
    
    const data = dashboardData.MonthlyStats.map((item) => {
      const convertedMonth = convertMonthNameToThai(item.MonthName || "");
      const result = {
        month: convertedMonth, // แปลงเป็นภาษาไทยและปีพ.ศ.
        monthYear: item.MonthYear || "",
        login: Number(item.LoginCount) || 0,
        reservation: Number(item.ReservationCount) || 0,
        reservationBottles: Number(item.ReservationBottles) || 0,
        reservationDoses: Number(item.ReservationDoses) || 0,
        reservationAmount: Number(item.ReservationAmount) || 0,
        saleOrder: Number(item.SaleOrderCount) || 0,
        saleOrderBottles: Number(item.SaleOrderBottles) || 0,
        saleOrderDoses: Number(item.SaleOrderDoses) || 0,
        saleOrderAmount: Number(item.SaleOrderAmount) || 0,
        requisition: Number(item.RequisitionCount) || 0,
        requisitionRequestBottles: Number(item.RequisitionRequestBottles) || 0,
        requisitionApprovedBottles: Number(item.RequisitionApprovedBottles) || 0,
        requisitionRequestDoses: Number(item.RequisitionRequestDoses) || 0,
        requisitionApprovedDoses: Number(item.RequisitionApprovedDoses) || 0,
        requisitionRequestAmount: Number(item.RequisitionRequestAmount) || 0,
        requisitionApprovedAmount: Number(item.RequisitionApprovedAmount) || 0,
        productionReceive: Number(item.ProductionReceiveCount) || 0,
        productionReceiveBottles: Number(item.ProductionReceiveBottles) || 0,
        productionReceiveDoses: Number(item.ProductionReceiveDoses) || 0,
        productionReceiveAmount: Number(item.ProductionReceiveAmount) || 0,
      };
      return result;
    });
    
    console.log("Chart Data for Line Chart:", data);
    console.log("Sample data point:", data[0]);
    return data;
  }, [dashboardData]);

  // ฟังก์ชันจัดกลุ่มวัคซีนตาม VaccineID
  const getVaccineGroup = (vaccineID) => {
    if (!vaccineID) return null;
    
    // วัคซีนโรคปากและเท้าเปื่อย โค-กระบือ
    if (['VAX1005', 'VAX1006', 'VAX1007', 'VAX1008', 'VAX1009', 'VAX1010', 'VAX1011'].includes(vaccineID)) {
      return 'โรคปากและเท้าเปื่อย โค-กระบือ';
    }
    
    // วัคซีนโรคปากและเท้าเปื่อยสุกร
    if (['VAX2003', 'VAX2004', 'VAX2005', 'VAX2006'].includes(vaccineID)) {
      return 'โรคปากและเท้าเปื่อยสุกร';
    }
    
    // อหิวาห์สุกร
    if (['VAX2001', 'VAX2002'].includes(vaccineID)) {
      return 'อหิวาห์สุกร';
    }
    
    return null; // ไม่ได้อยู่ในกลุ่มที่ต้องรวม
  };

  // ฟังก์ชันกำหนดสีให้กับวัคซีนแต่ละชนิด (ให้สีเดียวกันทุกกราฟ)
  const getVaccineColor = (vaccineName, index) => {
    // สีสำหรับกลุ่มที่รวมแล้ว
    const groupColors = {
      'โรคปากและเท้าเปื่อย โค-กระบือ': '#E53E3E',
      'โรคปากและเท้าเปื่อยสุกร': '#3182CE',
      'อหิวาห์สุกร': '#805AD5',
    };
    
    if (groupColors[vaccineName]) {
      return groupColors[vaccineName];
    }
    
    // สีสำหรับวัคซีนอื่นๆ (ใช้สีที่แตกต่างกัน)
    const defaultColors = [
      '#38A169', '#D69E2E', '#F56565', '#9F7AEA', '#ED8936',
      '#48BB78', '#ECC94B', '#FC8181', '#B794F4', '#F6AD55',
      '#68D391', '#F6E05E', '#FEB2B2', '#D6BCFA', '#FBD38D',
    ];
    
    return defaultColors[index % defaultColors.length];
  };

  // แปลงข้อมูลกราฟแยกตามชนิดวัคซีนให้เป็นรูปแบบที่ LineChart ใช้ได้
  const vaccineLineChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return {
        reservation: [],
        sale: [],
        requisition: [],
        productionReceive: [],
        reservationAmount: [],
        saleAmount: [],
        requisitionAmount: [],
        productionReceiveAmount: [],
        vaccineNames: [],
      };
    }

    // สร้าง array ของเดือนทั้งหมด
    const months = chartData.map(item => ({
      month: item.month,
      monthYear: item.monthYear,
    }));

    // ฟังก์ชันรวมยอดวัคซีนตามกลุ่ม
    const aggregateVaccineData = (rawData) => {
      if (!rawData || rawData.length === 0) return [];
      
      const grouped = {};
      
      rawData.forEach(item => {
        const groupName = getVaccineGroup(item.VaccineID);
        const key = groupName || item.VaccineNameTH;
        const monthYear = item.MonthYear;
        
        if (!grouped[monthYear]) {
          grouped[monthYear] = {};
        }
        
        if (!grouped[monthYear][key]) {
          grouped[monthYear][key] = {
            VaccineNameTH: key,
            TotalBottles: 0,
            TotalDoses: 0,
            TotalAmount: 0,
            RequestBottles: 0,
            RequestDoses: 0,
            RequestAmount: 0,
            ApprovedBottles: 0,
            ApprovedDoses: 0,
            ApprovedAmount: 0,
          };
        }
        
        grouped[monthYear][key].TotalBottles += Number(item.TotalBottles) || 0;
        grouped[monthYear][key].TotalDoses += Number(item.TotalDoses) || 0;
        grouped[monthYear][key].TotalAmount += Number(item.TotalAmount) || 0;
        grouped[monthYear][key].RequestBottles += Number(item.RequestBottles) || 0;
        grouped[monthYear][key].RequestDoses += Number(item.RequestDoses) || 0;
        grouped[monthYear][key].RequestAmount += Number(item.RequestAmount) || 0;
        grouped[monthYear][key].ApprovedBottles += Number(item.ApprovedBottles) || 0;
        grouped[monthYear][key].ApprovedDoses += Number(item.ApprovedDoses) || 0;
        grouped[monthYear][key].ApprovedAmount += Number(item.ApprovedAmount) || 0;
      });
      
      return grouped;
    };

    // ฟังก์ชันแปลงข้อมูลให้เป็นรูปแบบ LineChart
    const transformData = (rawData, doseKey, requestDoseKey = null, approvedDoseKey = null) => {
      if (!rawData || rawData.length === 0) return [];
      
      // รวมยอดวัคซีนตามกลุ่ม
      const aggregated = aggregateVaccineData(rawData);
      
      // หา vaccine names ทั้งหมด (หลังรวมกลุ่มแล้ว)
      const allVaccineNames = new Set();
      Object.values(aggregated).forEach(monthData => {
        Object.keys(monthData).forEach(vaccineName => {
          allVaccineNames.add(vaccineName);
        });
      });
      const vaccineNames = Array.from(allVaccineNames);
      
      // สร้างข้อมูลสำหรับแต่ละเดือน
      return months.map(monthItem => {
        const result = {
          month: monthItem.month,
          monthYear: monthItem.monthYear,
        };

        // สำหรับแต่ละชนิดวัคซีน (หลังรวมกลุ่มแล้ว)
        vaccineNames.forEach(vaccineName => {
          const monthData = aggregated[monthItem.monthYear];
          const dataForMonth = monthData && monthData[vaccineName];
          
          if (requestDoseKey && approvedDoseKey) {
            // สำหรับ Requisition (มี Request และ Approved)
            result[`${vaccineName}_Request`] = dataForMonth ? (Number(dataForMonth[requestDoseKey]) || 0) : 0;
            result[`${vaccineName}_Approved`] = dataForMonth ? (Number(dataForMonth[approvedDoseKey]) || 0) : 0;
          } else {
            // สำหรับกรณีปกติ
            result[vaccineName] = dataForMonth ? (Number(dataForMonth[doseKey]) || 0) : 0;
          }
        });

        return result;
      });
    };

    // แปลงข้อมูลจำนวนโดส
    const reservationDoses = transformData(vaccineChartData.reservation, 'TotalDoses');
    const saleDoses = transformData(vaccineChartData.sale, 'TotalDoses');
    const requisitionDoses = transformData(
      vaccineChartData.requisition,
      null,
      'RequestDoses',
      'ApprovedDoses'
    );
    const productionReceiveDoses = transformData(vaccineChartData.productionReceive, 'TotalDoses');

    // แปลงข้อมูลจำนวนเงิน
    const reservationAmount = transformData(vaccineChartData.reservation, 'TotalAmount');
    const saleAmount = transformData(vaccineChartData.sale, 'TotalAmount');
    const requisitionAmount = transformData(
      vaccineChartData.requisition,
      null,
      'RequestAmount',
      'ApprovedAmount'
    );
    const productionReceiveAmount = transformData(vaccineChartData.productionReceive, 'TotalAmount');

    // หา vaccine names ทั้งหมด (หลังรวมกลุ่มแล้ว)
    const getAllVaccineNames = (dataList) => {
      const allNames = new Set();
      dataList.forEach(rawData => {
        if (!rawData || rawData.length === 0) return;
        rawData.forEach(item => {
          const groupName = getVaccineGroup(item.VaccineID);
          const key = groupName || item.VaccineNameTH;
          allNames.add(key);
        });
      });
      return Array.from(allNames);
    };

    const allVaccineNames = getAllVaccineNames([
      vaccineChartData.reservation,
      vaccineChartData.sale,
      vaccineChartData.requisition,
      vaccineChartData.productionReceive,
    ]);

    return {
      reservation: reservationDoses,
      sale: saleDoses,
      requisition: requisitionDoses,
      productionReceive: productionReceiveDoses,
      reservationAmount: reservationAmount,
      saleAmount: saleAmount,
      requisitionAmount: requisitionAmount,
      productionReceiveAmount: productionReceiveAmount,
      vaccineNames: allVaccineNames,
    };
  }, [vaccineChartData, chartData]);

  // กรองข้อมูล Modal ตาม filter (text search)
  const filteredModalData = useMemo(() => {
    let filtered = modalData;
    if (modalTitle.includes("หน่วยงาน") && modalFilter.officeName) {
      const searchTerm = modalFilter.officeName.toLowerCase();
      filtered = modalData.filter(item => 
        (item.OfficeID && item.OfficeID.toLowerCase().includes(searchTerm)) ||
        (item.OfficeName && item.OfficeName.toLowerCase().includes(searchTerm))
      );
    }
    if (modalTitle.includes("จุดจำหน่าย") && modalFilter.pickupPointName) {
      const searchTerm = modalFilter.pickupPointName.toLowerCase();
      filtered = modalData.filter(item => 
        (item.PickupPointID && item.PickupPointID.toLowerCase().includes(searchTerm)) ||
        (item.PickupPointName && item.PickupPointName.toLowerCase().includes(searchTerm))
      );
    }
    if (modalTitle.includes("เบิกจ่าย") && modalFilter.warehouseName) {
      const searchTerm = modalFilter.warehouseName.toLowerCase();
      filtered = modalData.filter(item => 
        (item.FromWarehouseID && item.FromWarehouseID.toLowerCase().includes(searchTerm)) ||
        (item.FromWarehouseName && item.FromWarehouseName.toLowerCase().includes(searchTerm)) ||
        (item.ToWarehouseID && item.ToWarehouseID.toLowerCase().includes(searchTerm)) ||
        (item.ToWarehouseName && item.ToWarehouseName.toLowerCase().includes(searchTerm))
      );
    }
    if (modalTitle.includes("รับวัคซีนจากการผลิต") && modalFilter.warehouseName) {
      const searchTerm = modalFilter.warehouseName.toLowerCase();
      filtered = modalData.filter(item => 
        (item.WarehouseID && item.WarehouseID.toLowerCase().includes(searchTerm)) ||
        (item.WarehouseName && item.WarehouseName.toLowerCase().includes(searchTerm))
      );
    }
    return filtered;
  }, [modalData, modalFilter, modalTitle]);

  // คำนวณข้อมูล Pie Chart สำหรับแต่ละประเภท (แสดง 5 อันดับแรก + อื่นๆ)
  const pieChartData = useMemo(() => {
    const calculatePieData = (data, type) => {
      if (!data || data.length === 0) return { doses: [], amounts: [] };

      // รวมข้อมูลตามชนิดวัคซีน
      const vaccineMap = {};
      
      data.forEach(item => {
        const vaccineName = getVaccineGroup(item.VaccineID) || item.VaccineNameTH || item.VaccineNameEN || 'Unknown';
        const key = vaccineName;
        
        if (!vaccineMap[key]) {
          vaccineMap[key] = {
            name: key,
            doses: 0,
            amounts: 0,
          };
        }
        
        // สำหรับ Requisition ใช้ ApprovedDoses และ ApprovedAmount
        if (type === 'requisition') {
          vaccineMap[key].doses += Number(item.ApprovedDoses || 0);
          vaccineMap[key].amounts += Number(item.ApprovedAmount || 0);
        } else {
          vaccineMap[key].doses += Number(item.TotalDoses || 0);
          vaccineMap[key].amounts += Number(item.TotalAmount || 0);
        }
      });

      // แปลงเป็น array และเรียงตามยอด
      const vaccineArray = Object.values(vaccineMap);
      
      // เรียงตามจำนวนโดสหรือราคา
      const sortedByDoses = [...vaccineArray].sort((a, b) => b.doses - a.doses);
      const sortedByAmounts = [...vaccineArray].sort((a, b) => b.amounts - a.amounts);

      // เลือก 5 อันดับแรก
      const top5Doses = sortedByDoses.slice(0, 5);
      const top5Amounts = sortedByAmounts.slice(0, 5);

      // รวมส่วนที่เหลือเป็น "อื่นๆ"
      const otherDoses = sortedByDoses.slice(5).reduce((sum, item) => sum + item.doses, 0);
      const otherAmounts = sortedByAmounts.slice(5).reduce((sum, item) => sum + item.amounts, 0);

      const dosesData = [...top5Doses];
      if (otherDoses > 0) {
        dosesData.push({ name: 'อื่นๆ', doses: otherDoses, amounts: 0 });
      }

      const amountsData = [...top5Amounts];
      if (otherAmounts > 0) {
        amountsData.push({ name: 'อื่นๆ', doses: 0, amounts: otherAmounts });
      }

      return { doses: dosesData, amounts: amountsData };
    };

    return {
      reservation: calculatePieData(vaccineChartData.reservation, 'reservation'),
      sale: calculatePieData(vaccineChartData.sale, 'sale'),
      requisition: calculatePieData(vaccineChartData.requisition, 'requisition'),
      productionReceive: calculatePieData(vaccineChartData.productionReceive, 'productionReceive'),
    };
  }, [vaccineChartData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
        >
          <Text fontWeight="bold" mb={2}>{label}</Text>
          {payload.map((entry, index) => (
            <Text key={index} color={entry.color} fontSize="sm">
              {entry.name}: {entry.value.toLocaleString("th-TH")}
            </Text>
          ))}
        </Box>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
        >
          <Text fontWeight="bold" mb={2}>{data.name}</Text>
          <Text color={data.color} fontSize="sm">
            {data.dataKey === 'doses' 
              ? `จำนวนโดส: ${data.value.toLocaleString("th-TH")}`
              : `จำนวนเงิน: ${data.value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท`}
          </Text>
        </Box>
      );
    }
    return null;
  };

  // Custom Label Component สำหรับ Pie Chart (แสดงนอกกราฟ)
  const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, percent, fill }) => {
    const RADIAN = Math.PI / 180;
    // คำนวณตำแหน่งให้อยู่นอก pie chart
    const radius = outerRadius + 20; // ระยะห่างจากขอบ pie chart
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill={fill || "#333"}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="normal"
      >
        {`${name}: ${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  return (
    <Box>
      <StandardHeader />
      <Box p={5}>
        <StandardHeader
          title="Dashboard สถิติการเข้าใช้งานระบบ"
          bgColor="iBlue.600"
          icon={faChartLine}
        />

        {/* ฟิลเตอร์ */}
        <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" mb={5}>
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }} gap={4}>
            <GridItem>
              <FormControl>
                <FormLabel>เดือนเริ่มต้น</FormLabel>
                <Select
                  value={filters.StartMonth}
                  onChange={(e) => handleFilterChange("StartMonth", parseInt(e.target.value))}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <option key={month} value={month}>
                      {getThaiMonth(month)}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>ปีเริ่มต้น (พ.ศ.)</FormLabel>
                <Input
                  type="number"
                  value={filters.StartYear}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || currentBuddhistYear;
                    handleFilterChange("StartYear", value);
                  }}
                  placeholder={`${currentBuddhistYear}`}
                  min={2500}
                  max={2600}
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>เดือนสิ้นสุด</FormLabel>
                <Select
                  value={filters.EndMonth}
                  onChange={(e) => handleFilterChange("EndMonth", parseInt(e.target.value))}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <option key={month} value={month}>
                      {getThaiMonth(month)}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>ปีสิ้นสุด (พ.ศ.)</FormLabel>
                <Input
                  type="number"
                  value={filters.EndYear}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || currentBuddhistYear;
                    handleFilterChange("EndYear", value);
                  }}
                  placeholder={`${currentBuddhistYear}`}
                  min={2500}
                  max={2600}
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>หน่วยงาน</FormLabel>
                <Select
                  value={filters.OfficeID}
                  onChange={(e) => handleFilterChange("OfficeID", e.target.value)}
                >
                  <option value="">ทั้งหมด</option>
                  {offices.map((office) => (
                    <option key={office.OfficeID} value={office.OfficeID}>
                      {office.OfficeName}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
          </Grid>
          <HStack mt={4} justify="flex-end">
            <IconButton.Search
              leftIcon={<FontAwesomeIcon icon={faChartBar} />}
              onClick={handleSearch}
              isLoading={isLoading}
              label="ดูข้อมูล"
            />
          </HStack>
        </Box>

        {isLoading ? (
          <Center p={10}>
            <Spinner size="xl" />
          </Center>
        ) : dashboardData ? (
          <>
            {/* สรุปข้อมูล */}
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={5}>
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" cursor="pointer" _hover={{ boxShadow: "lg" }} onClick={() => handleViewOfficeDetails()}  bgColor="blue.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="blue.600">
                        <FontAwesomeIcon icon={faUsers} />
                        <Text fontWeight="bold">จำนวนหน่วยงานที่ใช้ระบบ</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="blue.600" cursor="pointer" _hover={{ color: "blue.500" }}>{dashboardData.OfficeCount || 0}</StatNumber>
                    <StatHelpText>คลิกเพื่อดูรายละเอียด</StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="cyan.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="cyan.600">
                        <FontAwesomeIcon icon={faSignInAlt} />
                        <Text fontWeight="bold">จำนวนครั้งที่ Login ทั้งหมด</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="cyan.600">
                      {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.LoginCount || 0), 0) || 0}
                    </StatNumber>
                    <StatHelpText>ครั้ง</StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="green.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="green.600">
                        <FontAwesomeIcon icon={faCalendarCheck} />
                        <Text fontWeight="bold">การสั่งจองวัคซีน</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="green.600">
                      {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.ReservationCount || 0), 0) || 0} 
                    </StatNumber>
                    <StatHelpText>
                      รายการ
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="yellow.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="yellow.600">
                        <FontAwesomeIcon icon={faShoppingCart} />
                        <Text fontWeight="bold">การขายวัคซีน</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="yellow.600">
                      {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.SaleOrderCount || 0), 0) || 0}
                    </StatNumber>
                    <StatHelpText>
                    รายการ
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="red.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="red.600">
                        <FontAwesomeIcon icon={faBoxOpen} />
                        <Text fontWeight="bold">การเบิก-จ่ายวัคซีน</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="red.600">
                      {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.RequisitionCount || 0), 0) || 0}
                    </StatNumber>
                    <StatHelpText>
                      รายการ
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="purple.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="purple.600">
                        <FontAwesomeIcon icon={faIndustry} />
                        <Text fontWeight="bold">การรับวัคซีนจากการผลิต</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="purple.600">
                      {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.ProductionReceiveCount || 0), 0) || 0}
                    </StatNumber>
                    <StatHelpText>
                      รายการ
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
            </Grid>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mb={5}>
        
              <GridItem>
            {/* กราฟสถิติการเข้าใช้ระบบรายเดือน */}
            <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" mb={5} border="1px solid" borderColor="blue.500">
              <HStack justify="space-between" mb={4}>
                <Heading size="md">
                  สถิติการเข้าใช้ระบบ                
                </Heading>
                <IconButton.Readmore
                  size="sm"
                    backgroundColor="blue.400"
                  onClick={() => handleViewOfficeDetails()}
                  label="รายละเอียดหน่วยงานที่เข้าใช้ระบบทั้งหมด"
                />
              </HStack>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      type="category"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={100}
                      tick={{ fontSize: 12 }}
                      allowDuplicatedCategory={false}
                    />
                    <YAxis
                      label={{
                        value: "จำนวนครั้ง",
                        angle: -90,
                        position: "insideLeft",
                        offset: -5,
                      }}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.toLocaleString("th-TH")}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                    <Bar 
                      dataKey="login" 
                      name="จำนวนครั้งที่ Login" 
                      fill="#3182CE"
                      style={{ cursor: 'pointer' }}
                      radius={[8, 8, 0, 0]}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill="#3182CE"
                          onClick={() => handleBarClick(entry, index)}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Center p={10}>
                  <Text color="gray.500">ไม่มีข้อมูลสำหรับแสดงกราฟ</Text>
                </Center>
              )}              
            </Box>
          </GridItem>
          <GridItem>
            {/* กราฟเปรียบเทียบกิจกรรม */}
            <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" mb={5} border="1px solid" borderColor="blue.500">
              <HStack justify="space-between" mb={4}>
                <Heading size="md">กิจกรรม</Heading>
                <HStack spacing={2}>
                  <IconButton.Readmore
                    size="sm"
                    backgroundColor="green.400"
                    onClick={() => handleViewReservationSaleDetails('reservation')}
                    label="การสั่งจอง"
                  />
                  <IconButton.Readmore
                    size="sm"
                    backgroundColor="yellow.400"
                    onClick={() => handleViewReservationSaleDetails('sale')}
                    label="การขาย"
                  />
                  <IconButton.Readmore
                    size="sm"
                    backgroundColor="red.400"
                    onClick={handleViewRequisitionDetails}
                    label="การเบิกจ่าย"
                  />
                  <IconButton.Readmore
                    size="sm"
                    backgroundColor="purple.400"
                    onClick={() => handleViewProductionReceiveDetails()}
                    label="การรับวัคซีนจากผลิต"
                  />
                </HStack>
              </HStack>
              {chartData && chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart 
                    data={chartData} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    onClick={(e) => {
                      // ตรวจสอบว่าคลิกที่บริเวณไหนของกราฟ
                      if (e && e.activePayload && e.activePayload.length > 0) {
                        const payload = e.activePayload[0].payload;
                        if (payload && payload.month) {
                          // ตรวจสอบว่าคลิกที่ Line ของการเบิกจ่ายหรือการรับวัคซีนจากผลิตหรือไม่
                          const clickedDataKey = e.activePayload[0].dataKey;
                          if (clickedDataKey === 'requisition') {
                            handleViewRequisitionDetails(payload.month);
                          } else if (clickedDataKey === 'productionReceive') {
                            handleViewProductionReceiveDetails(payload.month);
                          } else {
                            handleMonthClick(payload.month);
                          }
                        }
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      type="category"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={100}
                      tick={<CustomTick />}
                      allowDuplicatedCategory={false}
                    />
                    <YAxis
                      label={{
                        value: "จำนวนรายการ",
                        angle: -90,
                        position: "insideLeft",
                        offset: -5,
                      }}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.toLocaleString("th-TH")}
                      domain={[0, 'auto']}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                    <Line
                      type="monotone"
                      dataKey="reservation"
                      name="การสั่งจองวัคซีน"
                      stroke="#38A169"
                      strokeWidth={2}
                      dot={{ r: 4, cursor: 'pointer' }}
                      activeDot={{ r: 6, cursor: 'pointer' }}
                      connectNulls={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="saleOrder"
                      name="การขายวัคซีน"
                      stroke="#D69E2E"
                      strokeWidth={2}
                      dot={{ r: 4, cursor: 'pointer' }}
                      activeDot={{ r: 6, cursor: 'pointer' }}
                      connectNulls={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="requisition"
                      name="การเบิก-จ่ายวัคซีน"
                      stroke="#E53E3E"
                      strokeWidth={2}
                      dot={{ r: 4, cursor: 'pointer' }}
                      activeDot={{ r: 6, cursor: 'pointer' }}
                      connectNulls={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="productionReceive"
                      name="การรับวัคซีนจากการผลิต"
                      stroke="#805AD5"
                      strokeWidth={2}
                      dot={{ r: 4, cursor: 'pointer' }}
                      activeDot={{ r: 6, cursor: 'pointer' }}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Center p={10}>
                  <Text color="gray.500">
                    {chartData === undefined || chartData === null 
                      ? "กำลังโหลดข้อมูล..." 
                      : "ไม่มีข้อมูลสำหรับแสดงกราฟ"}
                  </Text>
                </Center>
              )}
            </Box>
              </GridItem>
              </Grid>
            {/* กราฟการจองวัคซีน */}
            <StandardHeader
              title="การจองวัคซีน"
              bgColor="iGreen.600"
              icon={faCalendarCheck}
            /> <br />
            
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={5}>
        
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="green.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="green.600">
                        <FontAwesomeIcon icon={faFileAlt} />
                        <Text fontWeight="bold">จำนวนรายการ</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="green.600">
                      {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.ReservationCount || 0), 0) || 0} 
                    </StatNumber>
                    <StatHelpText>
                      รายการ
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem> 
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="green.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="green.600">
                        <FontAwesomeIcon icon={faSyringe} />
                        <Text fontWeight="bold">จำนวนโดส</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="green.600">
                      {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.ReservationDoses || 0), 0).toLocaleString() || 0}
                    </StatNumber>
                    <StatHelpText>
                      โดส
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>       
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="green.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="green.600">
                        <FontAwesomeIcon icon={faMoneyBillWave} />
                        <Text fontWeight="bold">จำนวนเงิน</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="green.600">
                    {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.ReservationAmount || 0), 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'} 
                    </StatNumber>
                    <StatHelpText>
                        บาท
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>  
            </Grid>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mb={5}>
              {/* กราฟการจองวัคซีน - จำนวนโดส */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="green.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="green.600">การจองวัคซีน - จำนวนโดส</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="green.400"
                      onClick={() => handleViewReservationSaleDetailsWithMonth('reservation')}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {chartData && chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนโดส",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH")}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        <Bar 
                          dataKey="reservationDoses" 
                          name="จำนวนโดส" 
                          fill="#38A169"
                          style={{ cursor: 'pointer' }}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill="#38A169"
                              onClick={() => handleBarClickReservation(entry, index)}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                </Box>
              </GridItem>

              {/* กราฟการจองวัคซีน - จำนวนเงิน */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="green.500">
                  <Heading size="md" mb={4} color="green.600">การจองวัคซีน - จำนวนเงิน</Heading>
                  {chartData && chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนเงิน (บาท)",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        />
                        <Tooltip 
                          content={<CustomTooltip />}
                          formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        <Bar 
                          dataKey="reservationAmount" 
                          name="จำนวนเงิน" 
                          fill="#38A169"
                          style={{ cursor: 'pointer' }}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill="#38A169"
                              onClick={() => handleBarClickReservation(entry, index)}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                </Box>
              </GridItem>

              {/* กราฟการจองวัคซีน - จำนวนโดส (แยกตามชนิดวัคซีน)*/}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="green.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="green.600">การจองวัคซีน - จำนวนโดส (แยกตามชนิดวัคซีน)</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="green.400"
                      onClick={() => handleViewReservationSaleDetailsWithMonth('reservation')}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {vaccineLineChartData.reservation.length > 0 && vaccineLineChartData.vaccineNames.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart 
                        data={vaccineLineChartData.reservation} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        onClick={(e) => {
                          if (e && e.activePayload && e.activePayload.length > 0) {
                            const payload = e.activePayload[0].payload;
                            if (payload && payload.month) {
                              handleViewReservationSaleDetailsWithMonth('reservation', payload.month);
                            }
                          }
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={<CustomTick />}
                          allowDuplicatedCategory={false}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนโดส",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH")}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        {vaccineLineChartData.vaccineNames.map((vaccineName, index) => {
                          return (
                            <Line
                              key={vaccineName}
                              type="monotone"
                              dataKey={vaccineName}
                              name={vaccineName}
                              stroke={getVaccineColor(vaccineName, index)}
                              strokeWidth={2}
                              dot={{ r: 4, cursor: 'pointer' }}
                              activeDot={{ r: 6, cursor: 'pointer' }}
                              connectNulls={false}
                            />
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                  
                  {/* Pie Chart - จำนวนโดส */}
                  {pieChartData.reservation.doses.length > 0 && (
                    <Box mt={5}>
                      <Heading size="sm" mb={3} color="green.700">5 อันดับแรก - จำนวนโดส</Heading>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={pieChartData.reservation.doses}
                            dataKey="doses"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={<CustomPieLabel />}
                            labelLine={true}
                          >
                            {pieChartData.reservation.doses.map((entry, index) => {
                              // หา index ของ vaccineName ใน vaccineLineChartData.vaccineNames เพื่อให้สีตรงกับ Line Chart
                              const vaccineIndex = vaccineLineChartData.vaccineNames.indexOf(entry.name);
                              const colorIndex = vaccineIndex >= 0 ? vaccineIndex : index;
                              return (
                                <Cell key={`cell-${index}`} fill={getVaccineColor(entry.name, colorIndex)} />
                              );
                            })}
                          </Pie>
                          <Tooltip content={<CustomPieTooltip />} />
                          <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </GridItem>

              {/* กราฟการจองวัคซีน - จำนวนเงิน (แยกตามชนิดวัคซีน)*/}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="green.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="green.600">การจองวัคซีน - จำนวนเงิน (แยกตามชนิดวัคซีน)</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="green.400"
                      onClick={() => handleViewReservationSaleDetailsWithMonth('reservation')}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {vaccineLineChartData.reservationAmount.length > 0 && vaccineLineChartData.vaccineNames.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart 
                        data={vaccineLineChartData.reservationAmount} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        onClick={(e) => {
                          if (e && e.activePayload && e.activePayload.length > 0) {
                            const payload = e.activePayload[0].payload;
                            if (payload && payload.month) {
                              handleViewReservationSaleDetailsWithMonth('reservation', payload.month);
                            }
                          }
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={<CustomTick />}
                          allowDuplicatedCategory={false}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนเงิน (บาท)",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        />
                        <Tooltip 
                          content={<CustomTooltip />}
                          formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        {vaccineLineChartData.vaccineNames.map((vaccineName, index) => {
                          return (
                            <Line
                              key={vaccineName}
                              type="monotone"
                              dataKey={vaccineName}
                              name={vaccineName}
                              stroke={getVaccineColor(vaccineName, index)}
                              strokeWidth={2}
                              dot={{ r: 4, cursor: 'pointer' }}
                              activeDot={{ r: 6, cursor: 'pointer' }}
                              connectNulls={false}
                            />
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                  
                  {/* Pie Chart - จำนวนเงิน */}
                  {pieChartData.reservation.amounts.length > 0 && (
                    <Box mt={5}>
                      <Heading size="sm" mb={3} color="green.700">5 อันดับแรก - จำนวนเงิน</Heading>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={pieChartData.reservation.amounts}
                            dataKey="amounts"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={<CustomPieLabel />}
                          >
                            {pieChartData.reservation.amounts.map((entry, index) => {
                              // หา index ของ vaccineName ใน vaccineLineChartData.vaccineNames เพื่อให้สีตรงกับ Line Chart
                              const vaccineIndex = vaccineLineChartData.vaccineNames.indexOf(entry.name);
                              const colorIndex = vaccineIndex >= 0 ? vaccineIndex : index;
                              return (
                                <Cell key={`cell-${index}`} fill={getVaccineColor(entry.name, colorIndex)} />
                              );
                            })}
                          </Pie>
                          <Tooltip 
                            content={<CustomPieTooltip />}
                            formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                          />
                          <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </GridItem>
            </Grid>

            {/* กราฟการขายวัคซีน */}
            <StandardHeader
              title="การขายวัคซีน"
              bgColor="iYellow.600"
              icon={faShoppingCart}
            /> <br />
            
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={5}>
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="yellow.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="yellow.600">
                        <FontAwesomeIcon icon={faFileAlt} />
                        <Text fontWeight="bold">จำนวนรายการ</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="yellow.600">
                      {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.SaleOrderCount || 0), 0) || 0}
                    </StatNumber>
                    <StatHelpText>
                      รายการ
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="yellow.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="yellow.600">
                        <FontAwesomeIcon icon={faSyringe} />
                        <Text fontWeight="bold">จำนวนโดส</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="yellow.600">
                      {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.SaleOrderDoses || 0), 0).toLocaleString() || 0}
                    </StatNumber>
                    <StatHelpText>
                      โดส
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="yellow.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="yellow.600">
                        <FontAwesomeIcon icon={faMoneyBillWave} />
                        <Text fontWeight="bold">จำนวนเงิน</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="yellow.600">
                      {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.SaleOrderAmount || 0), 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                    </StatNumber>
                    <StatHelpText>
                      บาท
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
            </Grid>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mb={5}>

              {/* กราฟการขายวัคซีน - จำนวนโดส */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="yellow.500">
                  <Heading size="md" mb={4} color="yellow.600">การขายวัคซีน - จำนวนโดส</Heading>
                  {chartData && chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนโดส",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH")}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        <Bar 
                          dataKey="saleOrderDoses" 
                          name="จำนวนโดส" 
                          fill="#D69E2E"
                          style={{ cursor: 'pointer' }}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill="#D69E2E"
                              onClick={() => handleBarClickSale(entry, index)}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                </Box>
              </GridItem>

              {/* กราฟการขายวัคซีน - จำนวนเงิน */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="yellow.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="yellow.600">การขายวัคซีน - จำนวนเงิน</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="yellow.400"
                      onClick={() => handleViewReservationSaleDetailsWithMonth('sale')}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {chartData && chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนเงิน (บาท)",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        />
                        <Tooltip 
                          content={<CustomTooltip />}
                          formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        <Bar 
                          dataKey="saleOrderAmount" 
                          name="จำนวนเงิน" 
                          fill="#D69E2E"
                          style={{ cursor: 'pointer' }}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill="#D69E2E"
                              onClick={() => handleBarClickSale(entry, index)}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                </Box>
              </GridItem>

              {/* กราฟการขายวัคซีน - จำนวนโดส (แยกตามชนิดวัคซีน) */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="yellow.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="yellow.600">การขายวัคซีน - จำนวนโดส (แยกตามชนิดวัคซีน)</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="yellow.400"
                      onClick={() => handleViewReservationSaleDetailsWithMonth('sale')}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {vaccineLineChartData.sale.length > 0 && vaccineLineChartData.vaccineNames.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart 
                        data={vaccineLineChartData.sale} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        onClick={(e) => {
                          if (e && e.activePayload && e.activePayload.length > 0) {
                            const payload = e.activePayload[0].payload;
                            if (payload && payload.month) {
                              handleViewReservationSaleDetailsWithMonth('sale', payload.month);
                            }
                          }
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={<CustomTick />}
                          allowDuplicatedCategory={false}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนโดส",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH")}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        {vaccineLineChartData.vaccineNames.map((vaccineName, index) => {
                          return (
                            <Line
                              key={vaccineName}
                              type="monotone"
                              dataKey={vaccineName}
                              name={vaccineName}
                              stroke={getVaccineColor(vaccineName, index)}
                              strokeWidth={2}
                              dot={{ r: 4, cursor: 'pointer' }}
                              activeDot={{ r: 6, cursor: 'pointer' }}
                              connectNulls={false}
                            />
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                  
                  {/* Pie Chart - จำนวนโดส */}
                  {pieChartData.sale.doses.length > 0 && (
                    <Box mt={5}>
                      <Heading size="sm" mb={3} color="yellow.700">5 อันดับแรก - จำนวนโดส</Heading>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={pieChartData.sale.doses}
                            dataKey="doses"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={<CustomPieLabel />}
                          >
                            {pieChartData.sale.doses.map((entry, index) => {
                              // หา index ของ vaccineName ใน vaccineLineChartData.vaccineNames เพื่อให้สีตรงกับ Line Chart
                              const vaccineIndex = vaccineLineChartData.vaccineNames.indexOf(entry.name);
                              const colorIndex = vaccineIndex >= 0 ? vaccineIndex : index;
                              return (
                                <Cell key={`cell-${index}`} fill={getVaccineColor(entry.name, colorIndex)} />
                              );
                            })}
                          </Pie>
                          <Tooltip content={<CustomPieTooltip />} />
                          <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </GridItem>

              {/* กราฟการขายวัคซีน - จำนวนเงิน (แยกตามชนิดวัคซีน) */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="yellow.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="yellow.600">การขายวัคซีน - จำนวนเงิน (แยกตามชนิดวัคซีน)</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="yellow.400"
                      onClick={() => handleViewReservationSaleDetailsWithMonth('sale')}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {vaccineLineChartData.saleAmount.length > 0 && vaccineLineChartData.vaccineNames.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart 
                        data={vaccineLineChartData.saleAmount} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        onClick={(e) => {
                          if (e && e.activePayload && e.activePayload.length > 0) {
                            const payload = e.activePayload[0].payload;
                            if (payload && payload.month) {
                              handleViewReservationSaleDetailsWithMonth('sale', payload.month);
                            }
                          }
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={<CustomTick />}
                          allowDuplicatedCategory={false}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนเงิน (บาท)",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        />
                        <Tooltip 
                          content={<CustomTooltip />}
                          formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        {vaccineLineChartData.vaccineNames.map((vaccineName, index) => {
                          return (
                            <Line
                              key={vaccineName}
                              type="monotone"
                              dataKey={vaccineName}
                              name={vaccineName}
                              stroke={getVaccineColor(vaccineName, index)}
                              strokeWidth={2}
                              dot={{ r: 4, cursor: 'pointer' }}
                              activeDot={{ r: 6, cursor: 'pointer' }}
                              connectNulls={false}
                            />
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                  
                  {/* Pie Chart - จำนวนเงิน */}
                  {pieChartData.sale.amounts.length > 0 && (
                    <Box mt={5}>
                      <Heading size="sm" mb={3} color="yellow.700">5 อันดับแรก - จำนวนเงิน</Heading>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={pieChartData.sale.amounts}
                            dataKey="amounts"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={<CustomPieLabel />}
                          >
                            {pieChartData.sale.amounts.map((entry, index) => {
                              // หา index ของ vaccineName ใน vaccineLineChartData.vaccineNames เพื่อให้สีตรงกับ Line Chart
                              const vaccineIndex = vaccineLineChartData.vaccineNames.indexOf(entry.name);
                              const colorIndex = vaccineIndex >= 0 ? vaccineIndex : index;
                              return (
                                <Cell key={`cell-${index}`} fill={getVaccineColor(entry.name, colorIndex)} />
                              );
                            })}
                          </Pie>
                          <Tooltip 
                            content={<CustomPieTooltip />}
                            formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                          />
                          <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </GridItem>
            </Grid>

            {/* กราฟการเบิก-จ่ายวัคซีน */}
            <StandardHeader
              title="การเบิก-จ่ายวัคซีน"
              bgColor="iRed.600"
              icon={faShoppingCart}
            /> <br />
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={5}>              
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="red.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="red.600">
                        <FontAwesomeIcon icon={faFileAlt} />
                        <Text fontWeight="bold">จำนวนรายการ</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="red.600">
                      {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.RequisitionCount || 0), 0) || 0}
                    </StatNumber>
                    <StatHelpText>
                      รายการ
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>            
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="red.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="red.600">
                        <FontAwesomeIcon icon={faSyringe} />
                        <Text fontWeight="bold">จำนวนโดส (ขอเบิก / อนุมัติจ่าย)</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="red.600">
                    {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.RequisitionRequestDoses || 0), 0).toLocaleString() || 0} / {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.RequisitionApprovedDoses || 0), 0).toLocaleString() || 0}
                    </StatNumber>
                    <StatHelpText>
                      โดส
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>            
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="red.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="red.600">
                        <FontAwesomeIcon icon={faMoneyBillWave} />
                        <Text fontWeight="bold">จำนวนเงิน (ขอเบิก / อนุมัติจ่าย)</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="red.600">
                    {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.RequisitionRequestAmount || 0), 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}   /   {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.RequisitionApprovedAmount || 0), 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                    </StatNumber>
                    <StatHelpText>
                      บาท
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
            </Grid>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mb={5}>

              {/* กราฟการเบิก-จ่ายวัคซีน - จำนวนโดส */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="red.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="red.600">การเบิก-จ่ายวัคซีน - จำนวนโดส</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="red.400"
                      onClick={() => handleViewRequisitionDetails()}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {chartData && chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนโดส",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH")}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        <Bar 
                          dataKey="requisitionRequestDoses" 
                          name="จำนวนที่ขอเบิก" 
                          fill="#FC8181"
                          style={{ cursor: 'pointer' }}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-request-${index}`}
                              fill="#FC8181"
                              onClick={() => handleBarClickRequisition(entry, index)}
                            />
                          ))}
                        </Bar>
                        <Bar 
                          dataKey="requisitionApprovedDoses" 
                          name="จำนวนที่อนุมัติจ่าย" 
                          fill="#E53E3E"
                          style={{ cursor: 'pointer' }}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-approved-${index}`}
                              fill="#E53E3E"
                              onClick={() => handleBarClickRequisition(entry, index)}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                </Box>
              </GridItem>

              {/* กราฟการเบิก-จ่ายวัคซีน - จำนวนเงิน */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="red.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="red.600">การเบิก-จ่ายวัคซีน - จำนวนเงิน</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="red.400"
                      onClick={() => handleViewRequisitionDetails()}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {chartData && chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนเงิน (บาท)",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        />
                        <Tooltip 
                          content={<CustomTooltip />}
                          formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        <Bar 
                          dataKey="requisitionRequestAmount" 
                          name="จำนวนที่ขอเบิก" 
                          fill="#FC8181"
                          style={{ cursor: 'pointer' }}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-request-${index}`}
                              fill="#FC8181"
                              onClick={() => handleBarClickRequisition(entry, index)}
                            />
                          ))}
                        </Bar>
                        <Bar 
                          dataKey="requisitionApprovedAmount" 
                          name="จำนวนที่อนุมัติจ่าย" 
                          fill="#E53E3E"
                          style={{ cursor: 'pointer' }}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-approved-${index}`}
                              fill="#E53E3E"
                              onClick={() => handleBarClickRequisition(entry, index)}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                </Box>
              </GridItem>

              {/* กราฟการเบิก-จ่ายวัคซีน - จำนวนโดส (แยกตามชนิดวัคซีน) */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="red.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="red.600">การเบิก-จ่ายวัคซีน - จำนวนโดส (แยกตามชนิดวัคซีน)</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="red.400"
                      onClick={() => handleViewRequisitionDetails()}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {vaccineLineChartData.requisition.length > 0 && vaccineLineChartData.vaccineNames.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart 
                        data={vaccineLineChartData.requisition} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        onClick={(e) => {
                          if (e && e.activePayload && e.activePayload.length > 0) {
                            const payload = e.activePayload[0].payload;
                            if (payload && payload.month) {
                              handleViewRequisitionDetails(payload.month);
                            }
                          }
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={<CustomTick />}
                          allowDuplicatedCategory={false}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนโดส",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH")}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        {vaccineLineChartData.vaccineNames.map((vaccineName, index) => {
                          const baseColor = getVaccineColor(vaccineName, index);
                          return (
                            <React.Fragment key={vaccineName}>
                              <Line
                                type="monotone"
                                dataKey={`${vaccineName}_Request`}
                                name={`${vaccineName} (ขอเบิก)`}
                                stroke={baseColor}
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={{ r: 4, cursor: 'pointer' }}
                                activeDot={{ r: 6, cursor: 'pointer' }}
                                connectNulls={false}
                              />
                              <Line
                                type="monotone"
                                dataKey={`${vaccineName}_Approved`}
                                name={`${vaccineName} (อนุมัติ)`}
                                stroke={baseColor}
                                strokeWidth={2}
                                dot={{ r: 4, cursor: 'pointer' }}
                                activeDot={{ r: 6, cursor: 'pointer' }}
                                connectNulls={false}
                              />
                            </React.Fragment>
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                  
                  {/* Pie Chart - จำนวนโดส */}
                  {pieChartData.requisition.doses.length > 0 && (
                    <Box mt={5}>
                      <Heading size="sm" mb={3} color="red.700">5 อันดับแรก - จำนวนโดส (อนุมัติ)</Heading>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={pieChartData.requisition.doses}
                            dataKey="doses"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={<CustomPieLabel />}
                          >
                            {pieChartData.requisition.doses.map((entry, index) => {
                              // หา index ของ vaccineName ใน vaccineLineChartData.vaccineNames เพื่อให้สีตรงกับ Line Chart
                              const vaccineIndex = vaccineLineChartData.vaccineNames.indexOf(entry.name);
                              const colorIndex = vaccineIndex >= 0 ? vaccineIndex : index;
                              return (
                                <Cell key={`cell-${index}`} fill={getVaccineColor(entry.name, colorIndex)} />
                              );
                            })}
                          </Pie>
                          <Tooltip content={<CustomPieTooltip />} />
                          <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </GridItem>

              {/* กราฟการเบิก-จ่ายวัคซีน - จำนวนเงิน (แยกตามชนิดวัคซีน) */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="red.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="red.600">การเบิก-จ่ายวัคซีน - จำนวนเงิน (แยกตามชนิดวัคซีน)</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="red.400"
                      onClick={() => handleViewRequisitionDetails()}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {vaccineLineChartData.requisitionAmount.length > 0 && vaccineLineChartData.vaccineNames.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart 
                        data={vaccineLineChartData.requisitionAmount} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        onClick={(e) => {
                          if (e && e.activePayload && e.activePayload.length > 0) {
                            const payload = e.activePayload[0].payload;
                            if (payload && payload.month) {
                              handleViewRequisitionDetails(payload.month);
                            }
                          }
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={<CustomTick />}
                          allowDuplicatedCategory={false}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนเงิน (บาท)",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        />
                        <Tooltip 
                          content={<CustomTooltip />}
                          formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        {vaccineLineChartData.vaccineNames.map((vaccineName, index) => {
                          const baseColor = getVaccineColor(vaccineName, index);
                          return (
                            <React.Fragment key={vaccineName}>
                              <Line
                                type="monotone"
                                dataKey={`${vaccineName}_Request`}
                                name={`${vaccineName} (ขอเบิก)`}
                                stroke={baseColor}
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={{ r: 4, cursor: 'pointer' }}
                                activeDot={{ r: 6, cursor: 'pointer' }}
                                connectNulls={false}
                              />
                              <Line
                                type="monotone"
                                dataKey={`${vaccineName}_Approved`}
                                name={`${vaccineName} (อนุมัติ)`}
                                stroke={baseColor}
                                strokeWidth={2}
                                dot={{ r: 4, cursor: 'pointer' }}
                                activeDot={{ r: 6, cursor: 'pointer' }}
                                connectNulls={false}
                              />
                            </React.Fragment>
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                  
                  {/* Pie Chart - จำนวนเงิน */}
                  {pieChartData.requisition.amounts.length > 0 && (
                    <Box mt={5}>
                      <Heading size="sm" mb={3} color="red.700">5 อันดับแรก - จำนวนเงิน (อนุมัติ)</Heading>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={pieChartData.requisition.amounts}
                            dataKey="amounts"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={<CustomPieLabel />}
                          >
                            {pieChartData.requisition.amounts.map((entry, index) => {
                              // หา index ของ vaccineName ใน vaccineLineChartData.vaccineNames เพื่อให้สีตรงกับ Line Chart
                              const vaccineIndex = vaccineLineChartData.vaccineNames.indexOf(entry.name);
                              const colorIndex = vaccineIndex >= 0 ? vaccineIndex : index;
                              return (
                                <Cell key={`cell-${index}`} fill={getVaccineColor(entry.name, colorIndex)} />
                              );
                            })}
                          </Pie>
                          <Tooltip 
                            content={<CustomPieTooltip />}
                            formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                          />
                          <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </GridItem>
            </Grid>


            {/* กราฟการรับวัคซีนจากฝ่ายผลิต */}
            <StandardHeader
              title="การรับวัคซีนจากฝ่ายผลิต"
              bgColor="iPurple.600"
              icon={faShoppingCart}
            /> <br />
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={5}>
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="purple.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="purple.600">
                        <FontAwesomeIcon icon={faFileAlt} />
                        <Text fontWeight="bold">จำนวนรายการ</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="purple.600">
                      {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.ProductionReceiveCount || 0), 0) || 0}
                    </StatNumber>
                    <StatHelpText>
                      รายการ
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="purple.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="purple.600">
                        <FontAwesomeIcon icon={faSyringe} />
                        <Text fontWeight="bold">จำนวนโดส</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="purple.600">
                    {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.ProductionReceiveDoses || 0), 0).toLocaleString() || 0}
                    </StatNumber>
                    <StatHelpText>
                      โดส
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" bgColor="purple.50">
                  <Stat>
                    <StatLabel>
                      <HStack color="purple.600">
                        <FontAwesomeIcon icon={faMoneyBillWave} />
                        <Text fontWeight="bold">จำนวนเงิน</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="purple.600">
                    {dashboardData.MonthlyStats?.reduce((sum, item) => sum + (item.ProductionReceiveAmount || 0), 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'} 
                    </StatNumber>
                    <StatHelpText>
                      บาท
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
            </Grid>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mb={5}>

              {/* กราฟการรับวัคซีนจากฝ่ายผลิต - จำนวนโดส */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="purple.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="purple.600">การรับวัคซีนจากฝ่ายผลิต - จำนวนโดส</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="purple.400"
                      onClick={() => handleViewProductionReceiveDetails()}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {chartData && chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนโดส",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH")}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        <Bar 
                          dataKey="productionReceiveDoses" 
                          name="จำนวนโดส" 
                          fill="#805AD5"
                          style={{ cursor: 'pointer' }}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill="#805AD5"
                              onClick={() => handleBarClickProductionReceive(entry, index)}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                </Box>
              </GridItem>

              {/* กราฟการรับวัคซีนจากฝ่ายผลิต - จำนวนเงิน */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="purple.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="purple.600">การรับวัคซีนจากฝ่ายผลิต - จำนวนเงิน</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="purple.400"
                      onClick={() => handleViewProductionReceiveDetails()}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {chartData && chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนเงิน (บาท)",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        />
                        <Tooltip 
                          content={<CustomTooltip />}
                          formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        <Bar 
                          dataKey="productionReceiveAmount" 
                          name="จำนวนเงิน" 
                          fill="#805AD5"
                          style={{ cursor: 'pointer' }}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill="#805AD5"
                              onClick={() => handleBarClickProductionReceive(entry, index)}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                </Box>
              </GridItem>

              {/* กราฟการรับวัคซีนจากฝ่ายผลิต - จำนวนโดส (แยกตามชนิดวัคซีน) */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="purple.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="purple.600">การรับวัคซีนจากฝ่ายผลิต - จำนวนโดส (แยกตามชนิดวัคซีน)</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="purple.400"
                      onClick={() => handleViewProductionReceiveDetails()}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {vaccineLineChartData.productionReceive.length > 0 && vaccineLineChartData.vaccineNames.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart 
                        data={vaccineLineChartData.productionReceive} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        onClick={(e) => {
                          if (e && e.activePayload && e.activePayload.length > 0) {
                            const payload = e.activePayload[0].payload;
                            if (payload && payload.month) {
                              handleViewProductionReceiveDetails(payload.month);
                            }
                          }
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={<CustomTick />}
                          allowDuplicatedCategory={false}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนโดส",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH")}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        {vaccineLineChartData.vaccineNames.map((vaccineName, index) => {
                          return (
                            <Line
                              key={vaccineName}
                              type="monotone"
                              dataKey={vaccineName}
                              name={vaccineName}
                              stroke={getVaccineColor(vaccineName, index)}
                              strokeWidth={2}
                              dot={{ r: 4, cursor: 'pointer' }}
                              activeDot={{ r: 6, cursor: 'pointer' }}
                              connectNulls={false}
                            />
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                  
                  {/* Pie Chart - จำนวนโดส */}
                  {pieChartData.productionReceive.doses.length > 0 && (
                    <Box mt={5}>
                      <Heading size="sm" mb={3} color="purple.700">5 อันดับแรก - จำนวนโดส</Heading>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={pieChartData.productionReceive.doses}
                            dataKey="doses"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={<CustomPieLabel />}
                          >
                            {pieChartData.productionReceive.doses.map((entry, index) => {
                              // หา index ของ vaccineName ใน vaccineLineChartData.vaccineNames เพื่อให้สีตรงกับ Line Chart
                              const vaccineIndex = vaccineLineChartData.vaccineNames.indexOf(entry.name);
                              const colorIndex = vaccineIndex >= 0 ? vaccineIndex : index;
                              return (
                                <Cell key={`cell-${index}`} fill={getVaccineColor(entry.name, colorIndex)} />
                              );
                            })}
                          </Pie>
                          <Tooltip content={<CustomPieTooltip />} />
                          <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </GridItem>

              {/* กราฟการรับวัคซีนจากฝ่ายผลิต - จำนวนเงิน (แยกตามชนิดวัคซีน) */}
              <GridItem>
                <Box bg={cardBg} p={5} borderRadius="md" boxShadow="md" border="1px solid" borderColor="purple.500">
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md" color="purple.600">การรับวัคซีนจากฝ่ายผลิต - จำนวนเงิน (แยกตามชนิดวัคซีน)</Heading>
                    <IconButton.Readmore
                      size="sm"
                      backgroundColor="purple.400"
                      onClick={() => handleViewProductionReceiveDetails()}
                      label="ดูรายละเอียด"
                    />
                  </HStack>
                  {vaccineLineChartData.productionReceiveAmount.length > 0 && vaccineLineChartData.vaccineNames.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart 
                        data={vaccineLineChartData.productionReceiveAmount} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        onClick={(e) => {
                          if (e && e.activePayload && e.activePayload.length > 0) {
                            const payload = e.activePayload[0].payload;
                            if (payload && payload.month) {
                              handleViewProductionReceiveDetails(payload.month);
                            }
                          }
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          type="category"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={100}
                          tick={<CustomTick />}
                          allowDuplicatedCategory={false}
                        />
                        <YAxis
                          label={{
                            value: "จำนวนเงิน (บาท)",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        />
                        <Tooltip 
                          content={<CustomTooltip />}
                          formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        {vaccineLineChartData.vaccineNames.map((vaccineName, index) => {
                          return (
                            <Line
                              key={vaccineName}
                              type="monotone"
                              dataKey={vaccineName}
                              name={vaccineName}
                              stroke={getVaccineColor(vaccineName, index)}
                              strokeWidth={2}
                              dot={{ r: 4, cursor: 'pointer' }}
                              activeDot={{ r: 6, cursor: 'pointer' }}
                              connectNulls={false}
                            />
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center p={10}>
                      <Text color="gray.500">ไม่มีข้อมูล</Text>
                    </Center>
                  )}
                  
                  {/* Pie Chart - จำนวนเงิน */}
                  {pieChartData.productionReceive.amounts.length > 0 && (
                    <Box mt={5}>
                      <Heading size="sm" mb={3} color="purple.700">5 อันดับแรก - จำนวนเงิน</Heading>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={pieChartData.productionReceive.amounts}
                            dataKey="amounts"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={<CustomPieLabel />}
                          >
                            {pieChartData.productionReceive.amounts.map((entry, index) => {
                              // หา index ของ vaccineName ใน vaccineLineChartData.vaccineNames เพื่อให้สีตรงกับ Line Chart
                              const vaccineIndex = vaccineLineChartData.vaccineNames.indexOf(entry.name);
                              const colorIndex = vaccineIndex >= 0 ? vaccineIndex : index;
                              return (
                                <Cell key={`cell-${index}`} fill={getVaccineColor(entry.name, colorIndex)} />
                              );
                            })}
                          </Pie>
                          <Tooltip 
                            content={<CustomPieTooltip />}
                            formatter={(value) => value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " บาท"}
                          />
                          <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={12} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>
              </GridItem>
            </Grid>
          </>
        ) : (
          <Center p={10}>
            <Text color="gray.500">ไม่มีข้อมูล</Text>
          </Center>
        )}

        {/* Modal สำหรับแสดงรายละเอียด */}
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="6xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{modalTitle}</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {modalTitle.includes("กิจกรรมทั้งหมด") ? (
                // แสดงข้อมูลทุกประเภทกิจกรรมพร้อมกันด้วย Tabs
                <Tabs>
                  <TabList>
                    <Tab>หน่วยงานที่ใช้ระบบ</Tab>
                    <Tab>การสั่งจองวัคซีน</Tab>
                    <Tab>การขายวัคซีน</Tab>
                    <Tab>การเบิกจ่ายวัคซีน</Tab>
                    <Tab>การรับวัคซีนจากการผลิต</Tab>
                  </TabList>
                  <TabPanels>
                    {/* Tab 1: หน่วยงานที่ใช้ระบบ */}
                    <TabPanel>
                      <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                        <FormControl>
                          <FormLabel fontSize="sm">ค้นหาหน่วยงาน</FormLabel>
                          <Input
                            value={modalFilter.officeName}
                            onChange={(e) => setModalFilter({ ...modalFilter, officeName: e.target.value })}
                            placeholder="พิมพ์เพื่อค้นหา..."
                            size="sm"
                          />
                        </FormControl>
                      </Box>
                      {isLoadingDetails ? (
                        <Center p={10}>
                          <Spinner size="xl" />
                        </Center>
                      ) : monthActivityData.offices.length > 0 ? (
                        <TableContainer>
                          <Table variant="simple" size="sm">
                            <Thead>
                              <Tr>
                                <Th>รหัสหน่วยงาน</Th>
                                <Th>ชื่อหน่วยงาน</Th>
                                <Th>จำนวนผู้ใช้</Th>
                                <Th>จำนวนครั้งที่ Login</Th>
                                <Th>วันที่เข้าใช้ครั้งล่าสุด</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {monthActivityData.offices
                                .filter(item => 
                                  !modalFilter.officeName || 
                                  item.OfficeID?.toLowerCase().includes(modalFilter.officeName.toLowerCase()) ||
                                  item.OfficeName?.toLowerCase().includes(modalFilter.officeName.toLowerCase())
                                )
                                .map((item, index) => (
                                  <Tr key={index}>
                                    <Td>{item.OfficeID}</Td>
                                    <Td>{item.OfficeName || "-"}</Td>
                                    <Td>{item.UserCount || 0}</Td>
                                    <Td>{item.LoginCount || 0}</Td>
                                    <Td>{formatThaiDate(item.LastLoginDate)}</Td>
                                  </Tr>
                                ))}
                            </Tbody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Center p={10}>
                          <Text color="gray.500">ไม่มีข้อมูล</Text>
                        </Center>
                      )}
                    </TabPanel>
                    {/* Tab 2: การสั่งจองวัคซีน */}
                    <TabPanel>
                      <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                        <FormControl>
                          <FormLabel fontSize="sm">ค้นหาจุดจำหน่าย</FormLabel>
                          <Input
                            value={modalFilter.pickupPointName}
                            onChange={(e) => setModalFilter({ ...modalFilter, pickupPointName: e.target.value })}
                            placeholder="พิมพ์เพื่อค้นหา..."
                            size="sm"
                          />
                        </FormControl>
                      </Box>
                      {isLoadingDetails ? (
                        <Center p={10}>
                          <Spinner size="xl" />
                        </Center>
                      ) : monthActivityData.reservations.length > 0 ? (
                        <Accordion allowMultiple>
                          {monthActivityData.reservations
                            .filter(item => 
                              !modalFilter.pickupPointName || 
                              item.PickupPointID?.toLowerCase().includes(modalFilter.pickupPointName.toLowerCase()) ||
                              item.PickupPointName?.toLowerCase().includes(modalFilter.pickupPointName.toLowerCase())
                            )
                            .map((item, index) => {
                              const relatedVaccines = (monthActivityData.reservationVaccines || []).filter(v => v.PickupPointID === item.PickupPointID);
                              return (
                                <AccordionItem key={index}>
                                  <AccordionButton>
                                    <Box flex="1" textAlign="left">
                                      <HStack spacing={4}>
                                        <Text fontWeight="bold">{item.PickupPointID || "-"}</Text>
                                        <Text>{item.PickupPointName || "-"}</Text>
                                        <Text color="gray.500">รายการ: {item.TransactionCount || 0} | เกษตรกร: {item.FarmerCount || 0}</Text>
                                        <Text color="blue.600">โดส: {parseFloat(item.TotalDoses || 0).toLocaleString("th-TH")}</Text>
                                        <Text color="green.600">เงิน: {parseFloat(item.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</Text>
                                      </HStack>
                                    </Box>
                                    {relatedVaccines.length > 0 && <AccordionIcon />}
                                  </AccordionButton>
                                  {relatedVaccines.length > 0 && (
                                    <AccordionPanel pb={4}>
                                      <TableContainer>
                                        <Table variant="simple" size="sm">
                                          <Thead>
                                            <Tr>
                                              <Th>รหัสวัคซีน</Th>
                                              <Th>ชื่อวัคซีน</Th>
                                              <Th>จำนวนขวด</Th>
                                              <Th>จำนวนโดส</Th>
                                              <Th>จำนวนเงิน (บาท)</Th>
                                            </Tr>
                                          </Thead>
                                          <Tbody>
                                            {relatedVaccines.map((vaccine, vIndex) => (
                                              <Tr key={vIndex}>
                                                <Td>{vaccine.VaccineID || "-"}</Td>
                                                <Td>{vaccine.VaccineNameTH || vaccine.VaccineNameEN || "-"}</Td>
                                                <Td>{parseFloat(vaccine.TotalBottles || 0).toLocaleString("th-TH")}</Td>
                                                <Td>{parseFloat(vaccine.TotalDoses || 0).toLocaleString("th-TH")}</Td>
                                                <Td>{parseFloat(vaccine.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
                                              </Tr>
                                            ))}
                                          </Tbody>
                                        </Table>
                                      </TableContainer>
                                    </AccordionPanel>
                                  )}
                                </AccordionItem>
                              );
                            })}
                        </Accordion>
                      ) : (
                        <Center p={10}>
                          <Text color="gray.500">ไม่มีข้อมูล</Text>
                        </Center>
                      )}
                    </TabPanel>
                    {/* Tab 3: การขายวัคซีน */}
                    <TabPanel>
                      <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                        <FormControl>
                          <FormLabel fontSize="sm">ค้นหาจุดจำหน่าย</FormLabel>
                          <Input
                            value={modalFilter.pickupPointName}
                            onChange={(e) => setModalFilter({ ...modalFilter, pickupPointName: e.target.value })}
                            placeholder="พิมพ์เพื่อค้นหา..."
                            size="sm"
                          />
                        </FormControl>
                      </Box>
                      {isLoadingDetails ? (
                        <Center p={10}>
                          <Spinner size="xl" />
                        </Center>
                      ) : monthActivityData.sales.length > 0 ? (
                        <Accordion allowMultiple>
                          {monthActivityData.sales
                            .filter(item => 
                              !modalFilter.pickupPointName || 
                              item.PickupPointID?.toLowerCase().includes(modalFilter.pickupPointName.toLowerCase()) ||
                              item.PickupPointName?.toLowerCase().includes(modalFilter.pickupPointName.toLowerCase())
                            )
                            .map((item, index) => {
                              const relatedVaccines = (monthActivityData.saleVaccines || []).filter(v => v.PickupPointID === item.PickupPointID);
                              return (
                                <AccordionItem key={index}>
                                  <AccordionButton>
                                    <Box flex="1" textAlign="left">
                                      <HStack spacing={4}>
                                        <Text fontWeight="bold">{item.PickupPointID || "-"}</Text>
                                        <Text>{item.PickupPointName || "-"}</Text>
                                        <Text color="gray.500">รายการ: {item.TransactionCount || 0} | เกษตรกร: {item.FarmerCount || 0}</Text>
                                        <Text color="blue.600">โดส: {parseFloat(item.TotalDoses || 0).toLocaleString("th-TH")}</Text>
                                        <Text color="green.600">เงิน: {parseFloat(item.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</Text>
                                      </HStack>
                                    </Box>
                                    {relatedVaccines.length > 0 && <AccordionIcon />}
                                  </AccordionButton>
                                  {relatedVaccines.length > 0 && (
                                    <AccordionPanel pb={4}>
                                      <TableContainer>
                                        <Table variant="simple" size="sm">
                                          <Thead>
                                            <Tr>
                                              <Th>รหัสวัคซีน</Th>
                                              <Th>ชื่อวัคซีน</Th>
                                              <Th>จำนวนขวด</Th>
                                              <Th>จำนวนโดส</Th>
                                              <Th>จำนวนเงิน (บาท)</Th>
                                            </Tr>
                                          </Thead>
                                          <Tbody>
                                            {relatedVaccines.map((vaccine, vIndex) => (
                                              <Tr key={vIndex}>
                                                <Td>{vaccine.VaccineID || "-"}</Td>
                                                <Td>{vaccine.VaccineNameTH || vaccine.VaccineNameEN || "-"}</Td>
                                                <Td>{parseFloat(vaccine.TotalBottles || 0).toLocaleString("th-TH")}</Td>
                                                <Td>{parseFloat(vaccine.TotalDoses || 0).toLocaleString("th-TH")}</Td>
                                                <Td>{parseFloat(vaccine.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
                                              </Tr>
                                            ))}
                                          </Tbody>
                                        </Table>
                                      </TableContainer>
                                    </AccordionPanel>
                                  )}
                                </AccordionItem>
                              );
                            })}
                        </Accordion>
                      ) : (
                        <Center p={10}>
                          <Text color="gray.500">ไม่มีข้อมูล</Text>
                        </Center>
                      )}
                    </TabPanel>
                    {/* Tab 4: การเบิกจ่ายวัคซีน */}
                    <TabPanel>
                      <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                        <FormControl>
                          <FormLabel fontSize="sm">ค้นหาคลัง (ต้นทาง/ปลายทาง)</FormLabel>
                          <Input
                            value={modalFilter.warehouseName}
                            onChange={(e) => setModalFilter({ ...modalFilter, warehouseName: e.target.value })}
                            placeholder="พิมพ์เพื่อค้นหา..."
                            size="sm"
                          />
                        </FormControl>
                      </Box>
                      {isLoadingDetails ? (
                        <Center p={10}>
                          <Spinner size="xl" />
                        </Center>
                      ) : monthActivityData.requisitions.length > 0 ? (
                        <Accordion allowMultiple>
                          {monthActivityData.requisitions
                            .filter(item => 
                              !modalFilter.warehouseName || 
                              item.FromWarehouseID?.toLowerCase().includes(modalFilter.warehouseName.toLowerCase()) ||
                              item.FromWarehouseName?.toLowerCase().includes(modalFilter.warehouseName.toLowerCase()) ||
                              item.ToWarehouseID?.toLowerCase().includes(modalFilter.warehouseName.toLowerCase()) ||
                              item.ToWarehouseName?.toLowerCase().includes(modalFilter.warehouseName.toLowerCase())
                            )
                            .map((item, index) => {
                              const relatedVaccines = (monthActivityData.requisitionVaccines || []).filter(v => 
                                v.ToWarehouseID === item.ToWarehouseID && v.FromWarehouseID === item.FromWarehouseID
                              );
                              return (
                                <AccordionItem key={index}>
                                  <AccordionButton>
                                    <Box flex="1" textAlign="left">
                                      <HStack spacing={4}>
                                        <Text fontWeight="bold">{item.ToWarehouseID || "-"}</Text>
                                        <Text>{item.ToWarehouseName || "-"}</Text>
                                        <Text color="gray.500">จาก: {item.FromWarehouseName || "-"}</Text>
                                        <Text color="gray.500">รายการ: {item.TransactionCount || 0}</Text>
                                        <Text color="blue.600">ขอเบิก: {parseFloat(item.TotalRequestDoses || 0).toLocaleString("th-TH")} โดส</Text>
                                        <Text color="purple.600">อนุมัติ: {parseFloat(item.TotalApprovedDoses || 0).toLocaleString("th-TH")} โดส</Text>
                                        <Text color="green.600">เงิน: {parseFloat(item.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</Text>
                                      </HStack>
                                    </Box>
                                    {relatedVaccines.length > 0 && <AccordionIcon />}
                                  </AccordionButton>
                                  {relatedVaccines.length > 0 && (
                                    <AccordionPanel pb={4}>
                                      <TableContainer>
                                        <Table variant="simple" size="sm">
                                          <Thead>
                                            <Tr>
                                              <Th>รหัสวัคซีน</Th>
                                              <Th>ชื่อวัคซีน</Th>
                                              <Th>จำนวนขวด (ขอเบิก)</Th>
                                              <Th>จำนวนขวด (อนุมัติ)</Th>
                                              <Th>จำนวนโดส (ขอเบิก)</Th>
                                              <Th>จำนวนโดส (อนุมัติ)</Th>
                                              <Th>จำนวนเงิน (บาท)</Th>
                                            </Tr>
                                          </Thead>
                                          <Tbody>
                                            {relatedVaccines.map((vaccine, vIndex) => (
                                              <Tr key={vIndex}>
                                                <Td>{vaccine.VaccineID || "-"}</Td>
                                                <Td>{vaccine.VaccineNameTH || vaccine.VaccineNameEN || "-"}</Td>
                                                <Td>{parseFloat(vaccine.TotalRequestBottles || 0).toLocaleString("th-TH")}</Td>
                                                <Td>{parseFloat(vaccine.TotalApprovedBottles || 0).toLocaleString("th-TH")}</Td>
                                                <Td>{parseFloat(vaccine.TotalRequestDoses || 0).toLocaleString("th-TH")}</Td>
                                                <Td>{parseFloat(vaccine.TotalApprovedDoses || 0).toLocaleString("th-TH")}</Td>
                                                <Td>{parseFloat(vaccine.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
                                              </Tr>
                                            ))}
                                          </Tbody>
                                        </Table>
                                      </TableContainer>
                                    </AccordionPanel>
                                  )}
                                </AccordionItem>
                              );
                            })}
                        </Accordion>
                      ) : (
                        <Center p={10}>
                          <Text color="gray.500">ไม่มีข้อมูล</Text>
                        </Center>
                      )}
                    </TabPanel>
                    {/* Tab 5: การรับวัคซีนจากการผลิต */}
                    <TabPanel>
                      <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                        <FormControl>
                          <FormLabel fontSize="sm">ค้นหาคลัง</FormLabel>
                          <Input
                            value={modalFilter.warehouseName}
                            onChange={(e) => setModalFilter({ ...modalFilter, warehouseName: e.target.value })}
                            placeholder="พิมพ์เพื่อค้นหา..."
                            size="sm"
                          />
                        </FormControl>
                      </Box>
                      {isLoadingDetails ? (
                        <Center p={10}>
                          <Spinner size="xl" />
                        </Center>
                      ) : monthActivityData.productionReceives.length > 0 ? (
                        <Accordion allowMultiple>
                          {monthActivityData.productionReceives
                            .filter(item => 
                              !modalFilter.warehouseName || 
                              item.WarehouseID?.toLowerCase().includes(modalFilter.warehouseName.toLowerCase()) ||
                              item.WarehouseName?.toLowerCase().includes(modalFilter.warehouseName.toLowerCase())
                            )
                            .map((item, index) => {
                              const relatedVaccines = (monthActivityData.productionReceiveVaccines || []).filter(v => v.WarehouseID === item.WarehouseID);
                              return (
                                <AccordionItem key={index}>
                                  <AccordionButton>
                                    <Box flex="1" textAlign="left">
                                      <HStack spacing={4}>
                                        <Text fontWeight="bold">{item.WarehouseID || "-"}</Text>
                                        <Text>{item.WarehouseName || "-"}</Text>
                                        <Text color="gray.500">รายการ: {item.TransactionCount || 0}</Text>
                                        <Text color="blue.600">โดส: {parseFloat(item.TotalDoses || 0).toLocaleString("th-TH")}</Text>
                                        <Text color="green.600">เงิน: {parseFloat(item.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</Text>
                                      </HStack>
                                    </Box>
                                    {relatedVaccines.length > 0 && <AccordionIcon />}
                                  </AccordionButton>
                                  {relatedVaccines.length > 0 && (
                                    <AccordionPanel pb={4}>
                                      <TableContainer>
                                        <Table variant="simple" size="sm">
                                          <Thead>
                                            <Tr>
                                              <Th>รหัสวัคซีน</Th>
                                              <Th>ชื่อวัคซีน</Th>
                                              <Th>จำนวนขวด</Th>
                                              <Th>จำนวนโดส</Th>
                                              <Th>จำนวนเงิน (บาท)</Th>
                                            </Tr>
                                          </Thead>
                                          <Tbody>
                                            {relatedVaccines.map((vaccine, vIndex) => (
                                              <Tr key={vIndex}>
                                                <Td>{vaccine.VaccineID || "-"}</Td>
                                                <Td>{vaccine.VaccineNameTH || vaccine.VaccineNameEN || "-"}</Td>
                                                <Td>{parseFloat(vaccine.TotalBottles || 0).toLocaleString("th-TH")}</Td>
                                                <Td>{parseFloat(vaccine.TotalDoses || 0).toLocaleString("th-TH")}</Td>
                                                <Td>{parseFloat(vaccine.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
                                              </Tr>
                                            ))}
                                          </Tbody>
                                        </Table>
                                      </TableContainer>
                                    </AccordionPanel>
                                  )}
                                </AccordionItem>
                              );
                            })}
                        </Accordion>
                      ) : (
                        <Center p={10}>
                          <Text color="gray.500">ไม่มีข้อมูล</Text>
                        </Center>
                      )}
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              ) : (
                // แสดงข้อมูลแบบเดิม (ไม่ใช่กิจกรรมทั้งหมด)
                <>
                  {/* Filter Section */}
                  <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                    <HStack spacing={4}>
                      {modalTitle.includes("หน่วยงาน") && (
                        <FormControl>
                          <FormLabel fontSize="sm">ค้นหาหน่วยงาน</FormLabel>
                          <Input
                            value={modalFilter.officeName}
                            onChange={(e) => setModalFilter({ ...modalFilter, officeName: e.target.value })}
                            placeholder="พิมพ์เพื่อค้นหา..."
                            size="sm"
                          />
                        </FormControl>
                      )}
                      {modalTitle.includes("จุดจำหน่าย") && (
                        <FormControl>
                          <FormLabel fontSize="sm">ค้นหาจุดจำหน่าย</FormLabel>
                          <Input
                            value={modalFilter.pickupPointName}
                            onChange={(e) => setModalFilter({ ...modalFilter, pickupPointName: e.target.value })}
                            placeholder="พิมพ์เพื่อค้นหา..."
                            size="sm"
                          />
                        </FormControl>
                      )}
                      {modalTitle.includes("เบิกจ่าย") && (
                        <FormControl>
                          <FormLabel fontSize="sm">ค้นหาคลัง (ต้นทาง/ปลายทาง)</FormLabel>
                          <Input
                            value={modalFilter.warehouseName}
                            onChange={(e) => setModalFilter({ ...modalFilter, warehouseName: e.target.value })}
                            placeholder="พิมพ์เพื่อค้นหา..."
                            size="sm"
                          />
                        </FormControl>
                      )}
                    </HStack>
                  </Box>

                  {isLoadingDetails ? (
                    <Center p={10}>
                      <Spinner size="xl" />
                    </Center>
                  ) : filteredModalData.length > 0 ? (
                modalTitle.includes("หน่วยงาน") ? (
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>รหัสหน่วยงาน</Th>
                          <Th>ชื่อหน่วยงาน</Th>
                          <Th>จำนวนผู้ใช้</Th>
                          <Th>จำนวนครั้งที่ Login</Th>
                          <Th>วันที่เข้าใช้ครั้งล่าสุด</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filteredModalData.map((item, index) => (
                          <Tr key={index}>
                            <Td>{item.OfficeID}</Td>
                            <Td>{item.OfficeName || "-"}</Td>
                            <Td>{item.UserCount || 0}</Td>
                            <Td>{item.LoginCount || 0}</Td>
                            <Td>{formatThaiDate(item.LastLoginDate)}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Accordion allowMultiple>
                    {filteredModalData.map((item, index) => {
                      // กรองข้อมูลวัคซีนที่เกี่ยวข้องกับ item นี้
                      const relatedVaccines = modalVaccineData.filter(v => {
                        if (modalTitle.includes("จุดจำหน่าย")) {
                          return v.PickupPointID === item.PickupPointID;
                        } else if (modalTitle.includes("เบิกจ่าย")) {
                          return v.ToWarehouseID === item.ToWarehouseID && v.FromWarehouseID === item.FromWarehouseID;
                        } else if (modalTitle.includes("รับวัคซีนจากการผลิต")) {
                          return v.WarehouseID === item.WarehouseID;
                        }
                        return false;
                      });

                      return (
                        <AccordionItem key={index}>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <HStack spacing={4}>
                                {modalTitle.includes("จุดจำหน่าย") && (
                                  <>
                                    <Text fontWeight="bold">{item.PickupPointID || "-"}</Text>
                                    <Text>{item.PickupPointName || "-"}</Text>
                                    <Text color="gray.500">รายการ: {item.TransactionCount || 0} | เกษตรกร: {item.FarmerCount || 0}</Text>
                                    <Text color="blue.600">โดส: {parseFloat(item.TotalDoses || 0).toLocaleString("th-TH")}</Text>
                                    <Text color="green.600">เงิน: {parseFloat(item.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</Text>
                                  </>
                                )}
                                {modalTitle.includes("เบิกจ่าย") && (
                                  <>
                                    <Text fontWeight="bold">{item.ToWarehouseID || "-"}</Text>
                                    <Text>{item.ToWarehouseName || "-"}</Text>
                                    <Text color="gray.500">จาก: {item.FromWarehouseName || "-"}</Text>
                                    <Text color="gray.500">รายการ: {item.TransactionCount || 0}</Text>
                                    <Text color="blue.600">ขอเบิก: {parseFloat(item.TotalRequestDoses || 0).toLocaleString("th-TH")} โดส</Text>
                                    <Text color="purple.600">อนุมัติ: {parseFloat(item.TotalApprovedDoses || 0).toLocaleString("th-TH")} โดส</Text>
                                    <Text color="green.600">เงิน: {parseFloat(item.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</Text>
                                  </>
                                )}
                                {modalTitle.includes("รับวัคซีนจากการผลิต") && (
                                  <>
                                    <Text fontWeight="bold">{item.WarehouseID || "-"}</Text>
                                    <Text>{item.WarehouseName || "-"}</Text>
                                    <Text color="gray.500">รายการ: {item.TransactionCount || 0}</Text>
                                    <Text color="blue.600">โดส: {parseFloat(item.TotalDoses || 0).toLocaleString("th-TH")}</Text>
                                    <Text color="green.600">เงิน: {parseFloat(item.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</Text>
                                  </>
                                )}
                              </HStack>
                            </Box>
                            {relatedVaccines.length > 0 && <AccordionIcon />}
                          </AccordionButton>
                          {relatedVaccines.length > 0 && (
                            <AccordionPanel pb={4}>
                              <TableContainer>
                                <Table variant="simple" size="sm">
                                  <Thead>
                                    <Tr>
                                      <Th>รหัสวัคซีน</Th>
                                      <Th>ชื่อวัคซีน</Th>
                                      {modalTitle.includes("เบิกจ่าย") ? (
                                        <>
                                          <Th>จำนวนขวด (ขอเบิก)</Th>
                                          <Th>จำนวนขวด (อนุมัติ)</Th>
                                          <Th>จำนวนโดส (ขอเบิก)</Th>
                                          <Th>จำนวนโดส (อนุมัติ)</Th>
                                        </>
                                      ) : (
                                        <>
                                          <Th>จำนวนขวด</Th>
                                          <Th>จำนวนโดส</Th>
                                        </>
                                      )}
                                      <Th>จำนวนเงิน (บาท)</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {relatedVaccines.map((vaccine, vIndex) => (
                                      <Tr key={vIndex}>
                                        <Td>{vaccine.VaccineID || "-"}</Td>
                                        <Td>{vaccine.VaccineNameTH || vaccine.VaccineNameEN || "-"}</Td>
                                        {modalTitle.includes("เบิกจ่าย") ? (
                                          <>
                                            <Td>{parseFloat(vaccine.TotalRequestBottles || 0).toLocaleString("th-TH")}</Td>
                                            <Td>{parseFloat(vaccine.TotalApprovedBottles || 0).toLocaleString("th-TH")}</Td>
                                            <Td>{parseFloat(vaccine.TotalRequestDoses || 0).toLocaleString("th-TH")}</Td>
                                            <Td>{parseFloat(vaccine.TotalApprovedDoses || 0).toLocaleString("th-TH")}</Td>
                                          </>
                                        ) : (
                                          <>
                                            <Td>{parseFloat(vaccine.TotalBottles || 0).toLocaleString("th-TH")}</Td>
                                            <Td>{parseFloat(vaccine.TotalDoses || 0).toLocaleString("th-TH")}</Td>
                                          </>
                                        )}
                                        <Td>{parseFloat(vaccine.TotalAmount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </TableContainer>
                            </AccordionPanel>
                          )}
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                )
              ) : (
                <Center p={10}>
                  <Text color="gray.500">ไม่มีข้อมูล</Text>
                </Center>
              )}
                </>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
};

export default SystemUsageDashboard;

